import { API_METHODS } from '@compass/client/constants/api-methods';
import { AssetType } from '@compass/client/types/asset';
import { AuditStatus, AuditType } from '@compass/client/types/audit';
import { type CallInfo, CallType } from '@compass/client/types/call';
import { AssigneeSelect } from '@components/AssigneeSelect';
import { AudioPlayer, type AudioPlayerHandle } from '@components/AudioPlayer/AudioPlayer';
import { AuditFeedbackTable } from '@components/AuditFeedbackTable';
import { AuditorOutputFields } from '@components/AuditorOutputFields';
import { CloneCaseMenuItem } from '@components/CloneCaseMenuItem';
import { AudioErrorBoundary, TranscriptErrorBoundary } from '@components/ErrorBoundary';
import { StatusDropdown } from '@components/StatusDropdown';
import { Transcript } from '@components/Transcript/Transcript';
import { TwilioCallButton } from '@components/TwilioCallButton';
import { Button } from '@components/ui/button';
import { EditableSectionHeader } from '@components/ui/editable-section-header';
import { FieldGroupAccordion } from '@components/ui/field-group-accordion';
import { SearchableSelect } from '@components/ui/searchable-select';
import { Skeleton } from '@components/ui/skeleton';
import { SLATimer } from '@components/ui/sla-timer';
import { Tabs, TabsList, TabsTrigger } from '@components/ui/tabs';
import { SimpleTooltip } from '@components/ui/tooltip';
import { CaseState } from '@constants/caseStates';
import { useActiveScript } from '@hooks/useActiveScript';
import { useCallAssets } from '@hooks/useCallAssets';
import {
  buildDispositionPayload,
  type DispositionEdit,
  useCallDataEditor,
} from '@hooks/useCallDataEditor';
import { useCall, useCaseRecordCalls, useReviewTabSelection, useUpdateCall } from '@hooks/useCalls';
import { useCaseAudit, useCreateAuditFeedback, useUpdateAudit } from '@hooks/useCaseAudit';
import { useCaseRecordAssets } from '@hooks/useCaseRecordAssets';
import {
  useCase,
  useExecuteAction,
  useUpdateAssignment,
  useUpdateCaseOutput,
} from '@hooks/useCases';
import { useCaseWorkflow } from '@hooks/useCaseWorkflow';
import { useUpdateChartNotes } from '@hooks/useChartNotes';
import {
  useDispositions,
  useSelectableDispositions,
  useUpdateDispositionCode,
} from '@hooks/useDispositions';
import { useDownloadDocument } from '@hooks/useDownloadDocument';
import { useCaseRecordEligibilityChecks, useEligibilityCheck } from '@hooks/useEligibilityChecks';
import { usePayorEnhancedGroups } from '@hooks/usePayorEnhancedGroups';
import { useSchema } from '@hooks/useSchema';
import { useCallSummaries } from '@hooks/useSummary';
import { queryKeys } from '@services/query.config';
import { useStore } from '@store/index';
import { useQueryClient } from '@tanstack/react-query';
import {
  getHumanCallCompletionBlockReason,
  getNewCallBlockReason,
  hasCallDisposition,
} from '@utils/callGuard';
import {
  actionNameRoutesToHumanCall,
  actionRequiresCallDetails,
  canPerformAction,
  canWorkHumanAudit,
  executeCaseAction,
  getAvailableActionsForCaseState,
  handleCancelCallAction,
  handleCancelCaseAction,
} from '@utils/caseActions';
import { formatCaseId } from '@utils/cases';
import { isHumanAuditState, isHumanCallState } from '@utils/caseStateHelpers';
import { getAssetFileExtension } from '@utils/download';
import { buildFieldEnumMap } from '@utils/fields';
import { constructEmptyDataFromGroups } from '@utils/formSchema';
import { getNestedValue } from '@utils/nestedAccordion';
import {
  collectPhoneFieldErrors,
  getPhoneFieldError,
  getPhoneFromUISchema,
  getRandomPhoneIndex,
} from '@utils/phone';
import { buildCaseUrl } from '@utils/routes';
import { formatFieldName } from '@utils/string';
import { parseTimestampToSeconds } from '@utils/transcript';
import {
  Activity,
  Ban,
  ChevronsDownUp,
  FileText,
  History,
  Loader2,
  MoreVertical,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Share2,
  XCircle,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';

import { AddNoteDialog } from '@/components/AddNoteDialog';
import { AssigneeConfirmationDialog } from '@/components/AssigneeConfirmationDialog';
import { CallsTabs, PENDING_HUMAN_CALL_TAB_ID, type TabItem } from '@/components/CallsTabs';
import { CaseHistorySheet } from '@/components/CaseHistorySheet';
import {
  DispositionHelperPanel,
  DispositionSuggestion,
} from '@/components/cases/DispositionHelperPanel';
import { CaseSummaryView } from '@/components/CaseSummaryView';
import { CollapsibleSidebar, SidebarCollapseButton } from '@/components/CollapsibleSidebar';
import { DownloadDropdown } from '@/components/DownloadDropdown';
import type { FieldEvidence, FieldMetadata } from '@/components/fields/types';
import { FormattedDateTime } from '@/components/FormattedDateTime';
import { Breadcrumb, BreadcrumbPortal, HeaderActionsPortal } from '@/components/Header';
import { StatusConfirmationDialog } from '@/components/StatusConfirmationDialog';
import { Textarea } from '@/components/ui/textarea';
import { ACTION_LABELS, HUMAN_CALL_COMPLETION_ACTIONS } from '@/constants/actions';
import { CaseDetailsTabs, type CaseDetailsTabType } from '@/constants/caseDetails';
import { useCanEdit } from '@/hooks/useCanEdit';
import type { GetFieldSuggestionProps } from '@/hooks/useCaseInputStorage';
import { useCommittedDisposition } from '@/hooks/useCommittedDisposition';
import { useDownloadCaseRecords } from '@/hooks/useDownloadCaseRecords';
import { useDownloadSOB } from '@/hooks/useDownloadSOB';
import { useCaseRecordEmails } from '@/hooks/useEmails';
import { useEngineAgentId } from '@/hooks/useEngineAgentId';
import { useGenericTasks } from '@/hooks/useGenericTask';
import {
  useIsCaseProgramAdmin,
  useIsCurrentUserAssigned,
  usePermissionGate,
} from '@/hooks/usePermissions';
import { useShouldShowSummaryByDefault } from '@/hooks/useShouldShowSummaryByDefault';
import { useTexts } from '@/hooks/useText';
import { useWatchCaseRecord } from '@/hooks/useWatchCaseRecord';
import { buildFieldSchemaOrder } from '@/lib/disposition/fieldSchemaOrder';
import { filterOutputGroupsByPaths } from '@/lib/disposition/filterOutputGroups';
import { isProgramSOBEnabled } from '@/services/sobService';
import type { ActionDataParams } from '@/types/actions';
import type { CallsExportOption } from '@/types/export.types';
import { cn } from '@/utils';
import type { AssigneeUpdatePlan } from '@/utils/assigneeConfirmation';
import {
  countChangedLeaves,
  groupLeafCorrections,
  isChangedCorrection,
  resolveAuditBeforeValue,
  resolveCorrectionTarget,
} from '@/utils/auditCorrections';
import type { StatusUpdatePlan } from '@/utils/bulkStatusUpdate';
import { caseSchemaVersion } from '@/utils/caseSchemaVersion';
import { canAddNoteToCase } from '@/utils/caseStateHelpers';
import { copyToClipboard } from '@/utils/clipboard';
import { mergeExtractedData } from '@/utils/dataMerge';
import { getErrorButtonConfig } from '@/utils/errorButtonConfig';
import { stampCallSource } from '@/utils/evidence';
import { latestHandoffActions, visibleHandoffActions } from '@/utils/handoffField';
import { outputPathRootKey, setOutputAtPath } from '@/utils/outputPath';
import { buildRoutePath, removeOrgPrefix, removeProgPrefix } from '@/utils/routes';
import { getSelectedInputFieldValues } from '@/utils/table-config';
import type { TranscriptSegment } from '@/utils/transcript';

import { ErrorContainer } from '../../../components/ErrorContainer';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { Separator } from '../../../components/ui/separator';
import { CaseDetailsShell } from './CaseDetailsShell';
import { HumanAuditTab } from './humanAudit/HumanAuditTab';
import { HandoffNotesPanel } from './humanCall/HandoffNotesPanel';
import { buildHeardSeek, mergeHeardSeek } from './humanCall/heard/heardField';
import { HumanCallWorkspaceProvider } from './humanCall/HumanCallWorkspaceContext';
import { ReviewCallScriptColumn } from './humanCall/ReviewCallScriptColumn';
import {
  type ReviewOutputBoardHandle,
  ReviewOutputFieldBoard,
} from './humanCall/ReviewOutputFieldBoard';
import { useHumanCallEditState, useHumanCallVisibility } from './humanCall/useHumanCallScript';

/** Panes of the Review view's merged left column, in display order. */
type ReviewLeftTab = 'handoff' | 'transcript' | 'input';

/** Pane labels, shared by the switcher and the collapsed sidebar strip. */
const REVIEW_LEFT_TAB_LABEL: Record<ReviewLeftTab, string> = {
  handoff: 'Handoff actions',
  transcript: 'Transcript',
  input: 'Input',
};

export interface DefaultCaseDetailsPageProps {
  caseId: string;
  orgId?: string;
  progId?: string;
}

enum OutputFilter {
  All = 'all',
  Missed = 'missed',
  Required = 'required',
}

// Status actions that the details page surfaces in the "More options" menu
// instead of the status dropdown. Module-level constant so its reference stays
// stable across renders (StatusDropdown memoizes on it).
const DETAILS_PAGE_EXCLUDED_STATUS_ACTIONS = ['mark_as_terminated'];

export const DefaultCaseDetailsPage: React.FC<DefaultCaseDetailsPageProps> = ({
  caseId,
  orgId,
  progId,
}) => {
  const canEdit = useCanEdit();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    testModeEnabled,
    dialpad,
    selectedCallId,
    manuallySelectedCallId,
    setSelectedCallId,
    setManuallySelectedCallId,
    resetSelectedCallId,
    activeCall,
    stagedEscalations,
    clearStagedEscalations,
  } = useStore();
  const queryClient = useQueryClient();
  const isCurrentUserAssigned = useIsCurrentUserAssigned();
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const [evidenceHighlightRange, setEvidenceHighlightRange] = useState<{
    start: number;
    end: number;
  } | null>(null);
  const [activeTab, setActiveTab] = useState<CaseDetailsTabType>(CaseDetailsTabs.SUMMARY);
  const { downloadCaseRecords } = useDownloadCaseRecords();
  const { downloadIndividualSOB, isDownloading: isDownloadingSOB } = useDownloadSOB();
  const { downloadDoc } = useDownloadDocument();

  const [confirmationDialog, setConfirmationDialog] = useState<{
    isOpen: boolean;
    statusUpdatePlans: StatusUpdatePlan[];
  }>({
    isOpen: false,
    statusUpdatePlans: [],
  });

  // Assignee confirmation dialog state
  const [assigneeConfirmationDialog, setAssigneeConfirmationDialog] = useState<{
    isOpen: boolean;
    assigneeUpdatePlan: AssigneeUpdatePlan | null;
  }>({
    isOpen: false,
    assigneeUpdatePlan: null,
  });

  const audioPlayerRef = useRef<AudioPlayerHandle | null>(null);
  const hasInitialTabBeenSet = useRef(false);

  // State for Review tab accordion expansion
  const [reviewInputExpanded, setReviewInputExpanded] = useState<string[]>([]);
  const [reviewOutputExpanded, setReviewOutputExpanded] = useState<string[]>([]);

  // Transcript segments captured from the loaded transcript — used to snap heard markers.
  const [transcriptSegments, setTranscriptSegments] = useState<TranscriptSegment[]>([]);

  // Collapse state for the left-side Input drawer in the AI Audit view.
  const [isAiAuditInputCollapsed, setIsAiAuditInputCollapsed] = useState(true);
  // Collapse state for the right-side Feedback drawer in the AI Audit view.
  const [isAiAuditFeedbackCollapsed, setIsAiAuditFeedbackCollapsed] = useState(true);

  // Fire listCaseAudits + listCaseAuditFeedback whenever the case detail page is open.
  // The AI Audit tab is shown only when at least one audit row exists for this
  // case. Feedback is fetched eagerly so the drawer label can display the
  // current count without the user having to open the drawer first.
  const caseAudit = useCaseAudit({
    caseRecordId: caseId,
    auditsEnabled: true,
    feedbackEnabled: true,
  });
  const createAuditFeedback = useCreateAuditFeedback(caseId);
  const updateAudit = useUpdateAudit(caseId);

  // The human-audit entity the staged escalations get written onto when the auditor
  // routes the case to a human caller (see handleConfirmAction).
  const inProgressHumanAudit = useMemo(
    () =>
      caseAudit.audits?.find(
        a =>
          a.type === AuditType.AUDIT_TYPE_HUMAN &&
          a.status === AuditStatus.AUDIT_STATUS_IN_PROGRESS,
      ),
    [caseAudit.audits],
  );

  // Audit driving the case-level summary surfaces (corrections overlay,
  // pre-audit diff baseline, decision_rationale + recommendation banner).
  // Resolves to the audit whose id matches the active pill when an AI Audit
  // tab is selected; otherwise falls back to the latest audit so the
  // Summary/Call views keep showing the most recent verdict.
  const selectedAudit = useMemo(() => {
    return caseAudit.audits?.find(a => a.id === selectedCallId) ?? caseAudit.audits?.[0];
  }, [caseAudit.audits, selectedCallId]);

  // Selected audit's corrections, indexed by field path for O(1) row lookup.
  const correctionsByField = useMemo(() => {
    const map: Record<
      string,
      { action?: string; corrected_value?: unknown; original_value?: unknown; reasoning?: string }
    > = {};
    if (!selectedAudit?.corrections) return map;
    for (const c of selectedAudit.corrections) {
      if (c.field) {
        map[c.field] = {
          action: c.action,
          corrected_value: c.corrected_value,
          original_value: c.original_value,
          reasoning: c.reasoning,
        };
      }
    }
    return map;
  }, [selectedAudit]);

  // Toggle to filter the auditor output panel to changed fields only.
  const [showChangedOnly, setShowChangedOnly] = useState(false);
  const [hasInitialisedShowChangedOnly, setHasInitialisedShowChangedOnly] = useState(false);
  // Expanded group indices for the auditor output panel. Lifted out so the
  // Output header can render the Expand/Collapse All toggle. The Auditor
  // Output (reasoning) accordion is open by default.
  const [auditorGroupsExpanded, setAuditorGroupsExpanded] = useState<string[]>([
    'reasoning',
    'handoff',
  ]);

  // Output panel filter: All / Missed / Required (default All).
  const [outputFilter, setOutputFilter] = useState<OutputFilter>(OutputFilter.All);

  // State for Notes dialogs in kebab menu
  const [isAddNoteDialogOpen, setIsAddNoteDialogOpen] = useState(false);

  // State for History sheet
  const [isHistorySheetOpen, setIsHistorySheetOpen] = useState(false);

  // State for Notes sidebar (right column, collapsible)
  const [isNotesCollapsed, setIsNotesCollapsed] = useState(true);

  // State for Timeline sidebar (right column, collapsible)
  const [isTimelineCollapsed, setIsTimelineCollapsed] = useState(true);

  // State for the Output sidebar — only rendered in the Call Script layout, where Output
  // moves out of the column run and into a collapsible panel so the script has the room.
  // Starts COLLAPSED: the script is the surface the caller works through, and the fields
  // it captures are the same ones the Output board shows, so opening with both costs the
  // script width for a duplicate view. The caller expands it when they want the board.
  const [isOutputCollapsed, setIsOutputCollapsed] = useState(true);

  // State for the reference sidebar (handoff / transcript / input) on the LEFT of the Call
  // Script layout. Also starts expanded — the caller reads it while they work — but folds
  // away to a strip when the script needs the whole width.
  const [isReferenceCollapsed, setIsReferenceCollapsed] = useState(false);

  // Which pane of the merged left column is showing. Declared up here, beside the other
  // sidebar state, because `handleNavigateToCall` below needs to switch to the transcript
  // — its useCallback deps run during render, so a later `const` would be in the TDZ.
  const [reviewLeftTab, setReviewLeftTab] = useState<ReviewLeftTab>('handoff');

  // Permission check for viewing history
  const { disabled: historyDisabled } = usePermissionGate(
    API_METHODS.CaseRecordsService.GetCaseRecordHistory,
  );

  const { getSelectedProgram } = useStore();
  const selectedProgram = getSelectedProgram();
  // Fetch case details early to get workflow_type for correct workflow selection
  const { data: caseDetails, isLoading: caseLoading, error: caseError } = useCase(caseId);

  useWatchCaseRecord(caseId);

  // The case's own workflow, never the global selection, which another program
  // workflow can replace after the case loads (LA-8169).
  const selectedWorkflow = useCaseWorkflow(caseDetails?.workflow_id);

  // A transportation or unifiedmedbv human audit is worked by Program Admins and above
  // only (BV-2657): anyone else gets a read-only case and no Mark as Terminated.
  const isCaseProgramAdmin = useIsCaseProgramAdmin(
    caseDetails?.organization_id,
    caseDetails?.program_id,
  );
  const canWorkCaseHumanAudit = canWorkHumanAudit(
    caseDetails?.state,
    caseDetails?.workflow_type || selectedWorkflow?.workflow_type,
    isCaseProgramAdmin,
  );

  // The selected audit's handoff actions, as shown on the AI-audit panel. An audit with
  // none collapses its accordion item out of `visibleAccordionItems` (so
  // expand/collapse-all stays correct).
  const auditorHandoffActions = useMemo(
    // Case-level actions (field: "case") sort first and render with no field name; a
    // note-less one shows nothing, and an audit left with no action at all loses the
    // accordion.
    () => visibleHandoffActions(selectedAudit?.handoff_actions ?? []),
    [selectedAudit?.handoff_actions],
  );

  // Fetch schema from schema service
  const {
    inputGroups: rawSchemaInputGroups,
    outputGroups: schemaOutputGroups,
    schema: rawSchema,
  } = useSchema(selectedWorkflow?.id, caseSchemaVersion(caseDetails));

  const fieldEnumMap = useMemo(() => buildFieldEnumMap(rawSchema), [rawSchema]);

  // Replace primary_payer_name static options with live payor data for unifiedmedbv
  const { inputGroups: schemaInputGroups } = usePayorEnhancedGroups(
    rawSchemaInputGroups,
    selectedWorkflow?.workflow_type,
  );

  // Clean up audio when component unmounts
  useEffect(() => {
    return () => {
      audioPlayerRef.current?.stop();
      setAudioCurrentTime(0);
    };
  }, []);

  // Invalidate case and summary queries when page loads to ensure fresh data
  useEffect(() => {
    if (caseId) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.cases.byId(caseId),
      });
      // Also invalidate summary queries to ensure fresh data on refresh
      queryClient.invalidateQueries({
        queryKey: ['summaries', 'calls', caseId],
      });
      queryClient.invalidateQueries({
        queryKey: ['summaries', 'case', caseId],
      });
    }
  }, [caseId, queryClient]);

  // Fetch the list of calls for this case record
  const {
    data: callsData,
    isLoading: callsLoading,
    error: callsError,
  } = useCaseRecordCalls(caseId, {
    refetchInterval: 30000,
  });

  // Hook for updating call chart notes
  const { updateChartNotes } = useUpdateChartNotes(caseId);

  // Fetch the list of eligibility checks for this case record (only meaningful statuses)
  const {
    data: eligibilityChecksData,
    isLoading: eligibilityChecksLoading,
    error: eligibilityChecksError,
  } = useCaseRecordEligibilityChecks(
    caseId,
    {
      statuses: ['in_progress', 'completed', 'failed'],
    },
    {
      refetchInterval: 30000,
    },
  );

  const {
    data: genericTasksData,
    isLoading: genericTasksLoading,
    error: genericTasksError,
  } = useGenericTasks(caseId, {
    refetchInterval: 30000,
  });

  const {
    data: textsData,
    isLoading: textsLoading,
    error: textsError,
  } = useTexts(caseId, {
    refetchInterval: 30000,
  });

  const {
    data: emailsData,
    isLoading: emailsLoading,
    error: emailsError,
  } = useCaseRecordEmails(caseId);

  // Get the status of the selected call from callsData
  const selectedCallStatus = useMemo(() => {
    if (!selectedCallId || !callsData?.calls) return undefined;
    const selectedCall = callsData.calls.find(call => call.id === selectedCallId);
    return selectedCall?.status;
  }, [selectedCallId, callsData?.calls]);

  // ID of the most recent call — used to gate call-level editing so that only
  // the latest call is editable, never older ones.
  // MongoDB ObjectIDs embed a timestamp in their first 4 bytes, so ascending
  // string sort = chronological order. The max ID is the newest call.
  const latestCallId = useMemo(() => {
    if (!callsData?.calls?.length) return undefined;
    return callsData.calls.reduce<string | undefined>((latest, call) => {
      if (!call.id) return latest;
      return !latest || call.id > latest ? call.id : latest;
    }, undefined);
  }, [callsData?.calls]);

  // The case's most recent CALL_TYPE_HUMAN call, independent of whichever
  // call happens to be selected in Review. Needed to write a call-level
  // disposition when an agent picks one in the "Confirm Action" dialog for
  // human_call_mark_as_completed / human_call_mark_as_terminated (BI-1848):
  // by the time that dialog opens the call has already ended, so neither the
  // backend's `active_call_id` (cleared on call end — pkg/telephony/call.go)
  // nor the dialpad-driven `liveCallId` (also gone by then) can identify it.
  const latestHumanCall = useMemo(() => {
    if (!callsData?.calls?.length) return undefined;
    return callsData.calls.reduce<CallInfo | undefined>((latest, call) => {
      if (!call.id || call.type !== CallType.CALL_TYPE_HUMAN) return latest;
      return !latest?.id || call.id > latest.id ? call : latest;
    }, undefined);
  }, [callsData?.calls]);

  // Sequential-attempt guard: while the latest human call is live or ended
  // without a disposition, a new call may not be placed. The server enforces
  // the same rule on POST .../calls; this disables the Call button up front.
  const newCallBlockReason = useMemo(
    () => getNewCallBlockReason(latestHumanCall),
    [latestHumanCall],
  );

  // Best guess at the output the selected audit reviewed, for audit rows that predate
  // `output_snapshot`: the most recent call — of ANY type — created *before* the audit's
  // created_at that carries output data. The audit corrects that call's output, so it —
  // not the live case output_data, which may have been edited since — is the closest
  // available "before" value in the auditor diff view. Rows that carry a snapshot never
  // reach this; see auditFieldValue.
  //
  // Deliberately not restricted to AI calls: an AI audit can now run after a human call,
  // and diffing such an audit against the earlier AI call's output would show a "before"
  // the audit never saw — every field the human call changed would read as an audit
  // correction. Undefined when there's no audit or no preceding call with output, in
  // which case callers fall back to the current case output.
  const preAuditCallOutput = useMemo<Record<string, unknown> | undefined>(() => {
    const auditCreatedAt = selectedAudit?.created_at;
    const calls = callsData?.calls;
    if (!auditCreatedAt || !calls?.length) return undefined;

    const auditTime = new Date(auditCreatedAt).getTime();
    if (Number.isNaN(auditTime)) return undefined;

    let bestOutput: Record<string, unknown> | undefined;
    let bestTime = -Infinity;
    for (const call of calls) {
      if (!call.created_at) continue;
      const t = new Date(call.created_at).getTime();
      if (Number.isNaN(t) || t >= auditTime || t <= bestTime) continue;
      // A call that produced no output can't be the snapshot the audit read: letting it
      // win would blank the "before" side and hide the earlier call that does have one.
      const output = (call.output_data?.data || call.output_data) as
        | Record<string, unknown>
        | undefined;
      if (!output || Object.keys(output).length === 0) continue;
      bestTime = t;
      bestOutput = output;
    }
    return bestOutput;
  }, [selectedAudit, callsData?.calls]);

  // Fetch all call summaries for the case
  const { data: allCallSummaries = [] } = useCallSummaries(caseId, !!caseId, {
    refetchInterval: 30000,
  });

  // Get the summary for the currently selected call
  const callSummaryData = useMemo(() => {
    if (!selectedCallId || allCallSummaries.length === 0) {
      return null;
    }
    const summary = allCallSummaries.find(s => s.call_id === selectedCallId);
    console.debug('Selected call summary:', { selectedCallId, summary });
    return summary ? { summary } : null;
  }, [selectedCallId, allCallSummaries]);

  const executeAction = useExecuteAction();
  const updateAssignment = useUpdateAssignment();

  // Determine if Summary tab should be shown by default
  const showSummaryByDefault = useShouldShowSummaryByDefault(caseId);

  // The case's currently-live call (driven by the dialpad's real-time Twilio status via the
  // store) — drives the Human Call Script tab open/close instantly and is the call the script
  // reads/writes. Scoped to THIS case so another case's live call never leaks in.
  const liveCallId = activeCall?.caseRecordId === caseId ? activeCall.callId : undefined;

  const { callScriptAvailable } = useHumanCallVisibility({
    caseDetails,
    // Only consulted for workflows whose script is live-call-only (transportation).
    liveCallId,
  });

  // Handle transcript segment click to seek audio
  const handleSegmentClick = useCallback((time: number) => {
    audioPlayerRef.current?.seekTo(time);
  }, []);

  // Navigate from Summary tab to a specific call in the Review tab
  const handleNavigateToCall = useCallback(
    (callId: string, evidenceTimeStart?: string, evidenceTimeEnd?: string) => {
      setActiveTab(CaseDetailsTabs.REVIEW);
      setSelectedCallId(callId);
      setManuallySelectedCallId(callId);
      // Reveal the transcript. In the Call Script layout it lives behind a tab in the
      // merged left sidebar (defaulting to Handoff) and that sidebar can be folded away —
      // so seeking alone looked like nothing happened. Harmless in the classic layout,
      // where the transcript is its own column and this state is unused.
      setReviewLeftTab('transcript');
      setIsReferenceCollapsed(false);
      if (evidenceTimeStart) {
        const startSeconds = parseTimestampToSeconds(evidenceTimeStart);
        if (startSeconds !== null) {
          setAudioCurrentTime(startSeconds);
          audioPlayerRef.current?.seekTo(startSeconds);

          const endSeconds = evidenceTimeEnd ? parseTimestampToSeconds(evidenceTimeEnd) : null;
          setEvidenceHighlightRange(
            endSeconds !== null ? { start: startSeconds, end: endSeconds } : null,
          );
        }
      } else {
        setEvidenceHighlightRange(null);
      }
    },
    [setSelectedCallId, setManuallySelectedCallId],
  );

  // Enhance input groups with summaries from call summary data
  const inputGroups = useMemo(() => {
    if (schemaInputGroups.length === 0) {
      return [];
    }

    // Enhance groups with summaries from the call summary data
    const sectionSummaries = callSummaryData?.summary?.content?.section_summaries;

    if (sectionSummaries) {
      console.debug('Review tab - Enhancing input groups with summaries', {
        availableKeys: Object.keys(sectionSummaries),
        groupLabels: schemaInputGroups.map(g => g.label),
      });

      const enhanced = schemaInputGroups.map(group => ({
        ...group,
        summary: sectionSummaries[group.label],
      }));
      return enhanced;
    }

    return schemaInputGroups;
  }, [schemaInputGroups, callSummaryData]);

  // Enhance output groups with summaries from call summary data
  const outputGroups = useMemo(() => {
    if (schemaOutputGroups.length === 0) {
      return [];
    }

    // Enhance groups with summaries from the call summary data
    const sectionSummaries = callSummaryData?.summary?.content?.section_summaries;

    if (sectionSummaries) {
      console.debug('Review tab - Enhancing output groups with summaries', {
        availableKeys: Object.keys(sectionSummaries),
        groupLabels: schemaOutputGroups.map(g => g.label),
      });

      const enhanced = schemaOutputGroups.map(group => ({
        ...group,
        summary: sectionSummaries[group.label],
      }));
      return enhanced;
    }

    return schemaOutputGroups;
  }, [schemaOutputGroups, callSummaryData]);

  // The audit's changes (CHANGE / ADD / REMOVE), each resolved to the ROOT schema field
  // whose row can display it — a nested leaf correction (`drug.4.coverage.copay`) resolves
  // to `drug`. Corrections that resolve to nothing in the current schema are dropped:
  // they can't be rendered, so they must not inflate the pill or yield an empty panel when
  // the filter is toggled.
  //
  // Counting the CORRECTIONS rather than the root fields is deliberate. The auditor emits
  // one correction per entity instance, so a case with `drug.0…` through `drug.4…` plus
  // eight `procedure.N…` changes is 13 changes, not the 2 root fields it used to report —
  // and a run whose changes were ALL nested could previously resolve to no visible root
  // at all and read "Changed fields 0".
  const changedCorrections = useMemo(() => {
    const rows: { field: string; rootId: string; leafPath: string; correctedValue: unknown }[] = [];
    for (const c of selectedAudit?.corrections ?? []) {
      if (!isChangedCorrection(c)) continue;
      const target = resolveCorrectionTarget(outputGroups, c.field);
      if (!target) continue;
      rows.push({
        field: c.field!,
        rootId: target.rootId,
        leafPath: target.leafPath,
        correctedValue: c.corrected_value,
      });
    }
    return rows;
  }, [selectedAudit, outputGroups]);

  // Root field ids the "fields changed" filter narrows the panel to.
  const visibleChangedFieldIds = useMemo(
    () => new Set(changedCorrections.map(r => r.rootId)),
    [changedCorrections],
  );

  // Non-KEEP corrections on nested leaf paths, grouped under their root field
  // so AuditorOutputFields can render them as per-leaf diff rows.
  const leafCorrectionsByRoot = useMemo(
    () => groupLeafCorrections(selectedAudit?.corrections ?? [], outputGroups),
    [selectedAudit, outputGroups],
  );

  // Default the auditor panel to "fields changed" when the audit lists any
  // non-KEEP corrections — one-shot, so the user can still flip back to All
  // fields without us re-asserting the default on every render. Also expand
  // every field-group accordion on first load so the panel opens fully populated.
  useEffect(() => {
    if (hasInitialisedShowChangedOnly) return;
    if (outputGroups.length === 0) return;
    if (visibleChangedFieldIds.size > 0) {
      setShowChangedOnly(true);
    }
    setAuditorGroupsExpanded(prev => {
      const next = new Set(prev);
      outputGroups.forEach((_, i) => next.add(i.toString()));
      return Array.from(next);
    });
    setHasInitialisedShowChangedOnly(true);
  }, [hasInitialisedShowChangedOnly, outputGroups, visibleChangedFieldIds.size]);

  // Output groups the auditor panel will actually render, after applying the
  // "fields changed" filter. Memoised so the Output header (expand/collapse
  // toggle) and the AuditorOutputFields component share one computation.
  const auditorVisibleGroups = useMemo(() => {
    if (!showChangedOnly || visibleChangedFieldIds.size === 0) return outputGroups;
    return outputGroups
      .map(g => ({
        ...g,
        fields: g.fields.filter(f => visibleChangedFieldIds.has(f.id)),
      }))
      .filter(g => g.fields.length > 0);
  }, [outputGroups, showChangedOnly, visibleChangedFieldIds]);

  // Initialize Review tab accordions when groups are loaded (default expanded)
  useEffect(() => {
    if (inputGroups.length > 0) {
      setReviewInputExpanded(inputGroups.map((_, index) => index.toString()));
    }
  }, [inputGroups]);

  useEffect(() => {
    if (outputGroups.length > 0) {
      setReviewOutputExpanded(outputGroups.map((_, index) => index.toString()));
    }
  }, [outputGroups]);

  const loading = caseLoading || inputGroups.length === 0;

  // Reset selectedCallId when caseId changes to ensure correct call is selected for each case
  useEffect(() => {
    resetSelectedCallId();
  }, [caseId, resetSelectedCallId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      resetSelectedCallId();
      setConfirmationDialog({
        isOpen: false,
        statusUpdatePlans: [],
      });
    };
  }, [resetSelectedCallId]);

  // Set initial tab only once when data is first loaded
  useEffect(() => {
    if (
      callsLoading ||
      caseLoading ||
      eligibilityChecksLoading ||
      genericTasksLoading ||
      textsLoading ||
      hasInitialTabBeenSet.current ||
      callsData == undefined ||
      eligibilityChecksData == undefined ||
      genericTasksData == undefined ||
      textsData == undefined
    )
      return;

    // Review is the landing tab whenever a Call Script is on offer — that is where the
    // script column lives — for every viewer, not just the assignee. A ?callId= deep-link
    // lands there too, so the linked call is visible.
    const callIdFromQuery = searchParams.get('callId');
    if (callScriptAvailable || callIdFromQuery) {
      setActiveTab(CaseDetailsTabs.REVIEW);
    }
    // If there's a stored tab preference from dialpad AND it's for the same case, use it
    else if (dialpad.caseDetailsTab && dialpad.caseRecordId === caseId) {
      setActiveTab(dialpad.caseDetailsTab);
    } else if (showSummaryByDefault) {
      setActiveTab(CaseDetailsTabs.SUMMARY);
    } else {
      setActiveTab(CaseDetailsTabs.REVIEW);
    }
    hasInitialTabBeenSet.current = true;
  }, [
    showSummaryByDefault,
    callScriptAvailable,
    callsData,
    eligibilityChecksData,
    genericTasksData,
    textsData,
    eligibilityChecksLoading,
    genericTasksLoading,
    textsLoading,
    dialpad.caseDetailsTab,
    dialpad.caseRecordId,
    caseId,
    callsLoading,
    caseLoading,
    searchParams,
  ]);

  // Get editability from case record status flags and user permissions
  const getEditability = useMemo(() => {
    // Human audit (BI-1246 / BI-1323): OUTPUT editing is additionally gated on the
    // session being started — human_audit_in_progress flips true only after "Start
    // Human Audit". hermes enables can_update_* on human_audit entry, so without this
    // the editor would open in the "Human Audit Needed" phase (before Start), and edits
    // would go untracked. Other manual states (fill-info, human-call) are unaffected.
    //
    // INPUT editing is intentionally NOT gated on the session: input corrections
    // aren't part of the tracked human-audit corrections (those are output
    // OriginalValue → CorrectedValue), so input stays editable in the Summary view
    // throughout the human_audit state, matching the pre-redesign behavior.
    const sessionGate =
      caseDetails?.state !== CaseState.HUMAN_AUDIT ||
      caseDetails?.status_flags?.human_audit_in_progress === true;
    return {
      input:
        canEdit &&
        canWorkCaseHumanAudit &&
        !testModeEnabled &&
        (caseDetails?.status_flags?.can_update_input || false),
      output:
        canEdit &&
        canWorkCaseHumanAudit &&
        !testModeEnabled &&
        (caseDetails?.status_flags?.can_update_output || false) &&
        sessionGate,
    };
  }, [
    caseDetails?.status_flags,
    caseDetails?.state,
    canEdit,
    canWorkCaseHumanAudit,
    testModeEnabled,
  ]);

  // Toggle handlers for Review tab accordions
  const handleReviewInputToggleAll = () => {
    const allExpanded = reviewInputExpanded.length === inputGroups.length;
    if (allExpanded) {
      setReviewInputExpanded([]);
    } else {
      setReviewInputExpanded(inputGroups.map((_, index) => index.toString()));
    }
  };

  const handleReviewOutputToggleAll = () => {
    const allExpanded = reviewOutputExpanded.length === outputGroups.length;
    if (allExpanded) {
      setReviewOutputExpanded([]);
    } else {
      setReviewOutputExpanded(outputGroups.map((_, index) => index.toString()));
    }
  };

  const onRetryTask = (taskId: string) => {
    const statusUpdatePlan: StatusUpdatePlan = {
      action: 'create_task',
      caseIds: [caseId!],
      count: 1,
      actionDataParams: {
        task_id: taskId,
      },
    };
    setConfirmationDialog({
      isOpen: true,
      statusUpdatePlans: [statusUpdatePlan],
    });
  };

  // Get column config from workflow (if available)
  const columnConfig = useMemo(() => {
    const workflow = selectedWorkflow as any;
    return workflow?.column_config || [];
  }, [selectedWorkflow]);

  // Get first 2 selected input fields to display in header
  const firstTwoSelectedFields = useMemo(
    () => getSelectedInputFieldValues(columnConfig, caseDetails, 2),
    [columnConfig, caseDetails],
  );

  // Computed display conditions
  const hasCallsData =
    !callsLoading && !callsError && callsData?.calls && callsData.calls.length > 0;
  const hasEligibilityChecksData =
    !eligibilityChecksLoading &&
    !eligibilityChecksError &&
    eligibilityChecksData?.eligibility_checks &&
    eligibilityChecksData.eligibility_checks.length > 0;
  const hasGenericTasksData =
    !genericTasksLoading &&
    !genericTasksError &&
    genericTasksData?.tasks &&
    genericTasksData.tasks.length > 0;
  const hasTextsData =
    !textsLoading && !textsError && textsData?.texts && textsData.texts.length > 0;
  const hasEmailsData =
    !emailsLoading && !emailsError && emailsData?.emails && emailsData.emails.length > 0;
  const hasAuditData = (caseAudit.audits?.length ?? 0) > 0;
  // The attempts strip has nothing to show for a human call that has not been dialled yet,
  // which leaves the pre-call Call Script layout with nothing to select. A UI-only "Human
  // Call preview" pill stands in for the call about to be made; it disappears the moment
  // the real one exists, and the new call is auto-selected in its place (see the new-call
  // effect below).
  //
  // The pill belongs to THIS human-call phase, and a case can enter the phase more than
  // once — an auditor routing it back, or a workflow looping through another AI call, are
  // both ordinary. So the question is not "has this case ever had a human call" (which
  // suppressed the pill on every re-entry) but "is the latest human call still the newest
  // thing that happened". Anything created after it — another call of any type, or an audit
  // — means the case moved past that call and has since come back, so this phase is waiting
  // on a dial that has not happened.
  //
  // Ids are compared rather than timestamps because that is the ordering the strip itself
  // uses: Mongo ObjectIds are chronological, and calls and audits are already interleaved
  // by id in `unifiedTabItems`.
  const newestActivityId = useMemo(() => {
    const ids = [
      ...(callsData?.calls ?? []).map(call => call.id),
      ...(caseAudit.audits ?? []).map(audit => audit.id),
    ].filter((id): id is string => !!id);
    return ids.length > 0 ? ids.reduce((newest, id) => (id > newest ? id : newest)) : null;
  }, [callsData?.calls, caseAudit.audits]);
  const hasHumanCallThisPhase = !!latestHumanCall?.id && latestHumanCall.id === newestActivityId;
  const showPendingHumanCallItem = callScriptAvailable && !hasHumanCallThisPhase;

  // When the case enters a call-script phase awaiting a dial, land on the Human Call
  // Preview. The strip appends the pill last so it is already the DEFAULT selection — but
  // `determineReviewTabSelection` honours a manual selection first, and by this point the
  // rep almost always has one (moveToCallRecording sets it on hang-up, and any click on the
  // strip sets it). So the pill would appear while the view stayed on a spent call. Fires
  // only on the false -> true edge, so it never fights a selection made afterwards.
  const hadPendingHumanCallItem = useRef(showPendingHumanCallItem);
  useEffect(() => {
    const appeared = showPendingHumanCallItem && !hadPendingHumanCallItem.current;
    hadPendingHumanCallItem.current = showPendingHumanCallItem;
    if (!appeared) return;
    setSelectedCallId(PENDING_HUMAN_CALL_TAB_ID);
    setManuallySelectedCallId(PENDING_HUMAN_CALL_TAB_ID);
  }, [showPendingHumanCallItem, setSelectedCallId, setManuallySelectedCallId]);
  const shouldShowCallTabs =
    !caseLoading &&
    (showPendingHumanCallItem ||
      hasCallsData ||
      hasEligibilityChecksData ||
      hasGenericTasksData ||
      hasTextsData ||
      hasEmailsData ||
      hasAuditData) &&
    activeTab === CaseDetailsTabs.REVIEW;

  // Create unified tab items: mixed order by id (ascending)
  const unifiedTabItems = useMemo((): TabItem[] => {
    const allItems: TabItem[] = [];

    // Add eligibility checks
    if (eligibilityChecksData?.eligibility_checks) {
      eligibilityChecksData.eligibility_checks.forEach(eligibilityCheck => {
        allItems.push({
          type: 'eligibility',
          id: eligibilityCheck.id!,
          tabNumber: 0,
          data: eligibilityCheck,
        });
      });
    }

    // Add generic tasks
    if (genericTasksData?.tasks) {
      genericTasksData.tasks.forEach(task => {
        allItems.push({
          type: 'generic_task',
          id: task.id!,
          tabNumber: 0,
          data: task,
        });
      });
    }

    // Add texts
    if (textsData?.texts) {
      textsData.texts.forEach(text => {
        allItems.push({
          type: 'text',
          id: text.id!,
          tabNumber: 0,
          data: text,
        });
      });
    }

    // Add emails
    if (emailsData?.emails) {
      emailsData.emails.forEach(email => {
        allItems.push({
          type: 'email',
          id: email.id!,
          tabNumber: 0,
          data: email,
        });
      });
    }

    // Add calls
    if (callsData?.calls) {
      callsData.calls.forEach(call => {
        allItems.push({
          type: 'call',
          id: call.id!,
          tabNumber: 0,
          data: call,
        });
      });
    }

    // Add audits on the case; the id-based sort below interleaves them with
    // calls/eligibility checks chronologically. AI audits always show; HUMAN
    // audits (BI-1246) get their own tab type and appear while IN_PROGRESS (the
    // live editing workspace) or once COMPLETED (the corrections diff). Other
    // statuses (e.g. SKIPPED/superseded) are hidden.
    if (caseAudit.audits) {
      caseAudit.audits.forEach(audit => {
        if (!audit.id) return;
        const isHuman = audit.type === AuditType.AUDIT_TYPE_HUMAN;
        if (
          isHuman &&
          audit.status !== AuditStatus.AUDIT_STATUS_COMPLETED &&
          audit.status !== AuditStatus.AUDIT_STATUS_IN_PROGRESS
        ) {
          return;
        }
        allItems.push({
          type: isHuman ? 'human_audit' : 'ai_audit',
          id: audit.id,
          tabNumber: 0,
          data: audit,
        });
      });
    }

    // Sort all items by id (ascending) and assign sequential tab numbers
    allItems.sort((a, b) => a.id.localeCompare(b.id));

    // The pending attempt is the NEXT thing to happen, so it goes last regardless of the
    // id sort (its sentinel id is not chronological). Being last also makes it the strip's
    // default selection — `useReviewTabSelection` picks the newest tab — which is what puts
    // the pre-call script on screen without a click.
    if (showPendingHumanCallItem) {
      allItems.push({
        type: 'pending_human_call',
        id: PENDING_HUMAN_CALL_TAB_ID,
        tabNumber: 0,
        data: { id: PENDING_HUMAN_CALL_TAB_ID },
      });
    }

    // Update tab numbers after sorting
    return allItems.map((item, index) => ({
      ...item,
      tabNumber: index + 1,
    }));
  }, [
    eligibilityChecksData?.eligibility_checks,
    callsData?.calls,
    genericTasksData?.tasks,
    textsData?.texts,
    emailsData?.emails,
    caseAudit.audits,
    showPendingHumanCallItem,
  ]);

  // Navigate to the AI Audit tab when the latest evidence on a field is
  // audit-sourced (BI-839). The audit_id is stored as TabItem.id for the
  // ai_audit tab, so we prefer the exact match against the source audit;
  // fall back to any ai_audit tab when the specific audit_id isn't on the
  // case (stale evidence pointing at a deleted audit row).
  const handleNavigateToAudit = useCallback(
    (auditId: string) => {
      const exactMatch = unifiedTabItems.find(
        item => item.type === 'ai_audit' && item.id === auditId,
      );
      const anyAuditTab = exactMatch ?? unifiedTabItems.find(item => item.type === 'ai_audit');
      if (!anyAuditTab) return;
      setActiveTab(CaseDetailsTabs.REVIEW);
      setSelectedCallId(anyAuditTab.id);
      setManuallySelectedCallId(anyAuditTab.id);
      setEvidenceHighlightRange(null);
    },
    [unifiedTabItems, setSelectedCallId, setManuallySelectedCallId],
  );

  // Use the hook to manage automatic tab selection (eligibility checks + calls)
  useReviewTabSelection(unifiedTabItems);

  // Auto-navigate to the latest call when a new call appears.
  // Skip the initial population of callsData — only react to subsequent increases —
  // otherwise the first load races against the Summary tab and yanks the user to Review.
  const prevCallCountRef = useRef(0);
  const hasInitializedCallCountRef = useRef(false);
  useEffect(() => {
    if (callsData == undefined) return;
    const currentCount = callsData.calls?.length ?? 0;

    if (!hasInitializedCallCountRef.current) {
      hasInitializedCallCountRef.current = true;
      prevCallCountRef.current = currentCount;
      return;
    }

    const prevCount = prevCallCountRef.current;
    prevCallCountRef.current = currentCount;

    if (currentCount > prevCount && currentCount > 0) {
      const latestCallId = callsData.calls?.[callsData.calls.length - 1]?.id;
      if (latestCallId) {
        setSelectedCallId(latestCallId);
        setManuallySelectedCallId(latestCallId);
        setSearchParams(
          prev => {
            const params = new URLSearchParams(prev);
            params.set('callId', latestCallId);
            return params;
          },
          { replace: true, state: location.state },
        );
      }
    }
  }, [callsData, setSelectedCallId, setManuallySelectedCallId, setSearchParams, location.state]);

  // Human audit (BI-1246): when polling surfaces an IN-PROGRESS human-audit
  // session, pull the auditor into its workspace if they're on the Summary or
  // Review tab (mirrors how a live human call opens its own tab). Fires once per
  // audit id, so after the initial switch the user can navigate away without
  // being yanked back; never interrupts a live Human Call tab.
  const humanAuditAutoSwitchedRef = useRef<string | null>(null);
  const humanAuditInitializedRef = useRef(false);
  useEffect(() => {
    // Wait for the audits' first load so the initial population isn't mistaken
    // for a transition.
    if (caseAudit.isLoading) return;

    const liveHumanAudit = unifiedTabItems.find(
      item =>
        item.type === 'human_audit' &&
        (item.data as { status?: AuditStatus })?.status === AuditStatus.AUDIT_STATUS_IN_PROGRESS,
    );

    // First settled render: remember any already-active audit as "seen" and do
    // NOT switch. Opening a case (e.g. landing on Summary) must never transport
    // the user to the last human audit — only a genuine in-session transition
    // (a new in-progress audit appearing via polling) should.
    if (!humanAuditInitializedRef.current) {
      humanAuditInitializedRef.current = true;
      if (liveHumanAudit) humanAuditAutoSwitchedRef.current = liveHumanAudit.id;
      return;
    }

    if (!liveHumanAudit) return;
    if (humanAuditAutoSwitchedRef.current === liveHumanAudit.id) return;
    // Only auto-open from Summary/Review — don't interrupt a live Human Call.
    if (activeTab !== CaseDetailsTabs.SUMMARY && activeTab !== CaseDetailsTabs.REVIEW) return;
    // Already viewing it: record so we don't re-trigger, then stop.
    if (activeTab === CaseDetailsTabs.REVIEW && selectedCallId === liveHumanAudit.id) {
      humanAuditAutoSwitchedRef.current = liveHumanAudit.id;
      return;
    }
    humanAuditAutoSwitchedRef.current = liveHumanAudit.id;
    setActiveTab(CaseDetailsTabs.REVIEW);
    setSelectedCallId(liveHumanAudit.id);
    setManuallySelectedCallId(liveHumanAudit.id);
  }, [
    caseAudit.isLoading,
    unifiedTabItems,
    activeTab,
    selectedCallId,
    setSelectedCallId,
    setManuallySelectedCallId,
  ]);

  // Sync selected call ID with query params when in Review tab
  useEffect(() => {
    // The pending-attempt pill is UI-only — a `?callId=` pointing at its sentinel would
    // deep-link to something that exists only while the case has no call.
    if (
      activeTab === CaseDetailsTabs.REVIEW &&
      selectedCallId &&
      selectedCallId !== PENDING_HUMAN_CALL_TAB_ID
    ) {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev);
          params.set('callId', selectedCallId);
          return params;
        },
        { replace: true, state: location.state },
      );
    }
  }, [activeTab, selectedCallId, setSearchParams, location.state]);
  // Read call ID from query params on mount and when unifiedTabItems change
  useEffect(() => {
    const callIdFromQuery = searchParams.get('callId');
    if (!callIdFromQuery || unifiedTabItems.length === 0) return;

    // If the user has already manually selected a call that doesn't match the URL yet
    // (URL sync lags one render behind the tab click), don't override their selection.
    if (manuallySelectedCallId && callIdFromQuery !== manuallySelectedCallId) return;

    if (callIdFromQuery !== selectedCallId) {
      const itemExists = unifiedTabItems.find(item => item.id === callIdFromQuery);
      if (itemExists) {
        setSelectedCallId(callIdFromQuery);
        setManuallySelectedCallId(callIdFromQuery);
      }
    }
  }, [
    unifiedTabItems,
    selectedCallId,
    manuallySelectedCallId,
    searchParams,
    setSelectedCallId,
    setManuallySelectedCallId,
  ]);

  // Determine the selected tab item and its data
  const selectedTabItem = useMemo(() => {
    return unifiedTabItems.find(item => item.id === selectedCallId);
  }, [unifiedTabItems, selectedCallId]);

  // Fetch the selected call details (only when a call is selected)
  const { data: selectedCallData, error: callError } = useCall(
    caseId,
    selectedTabItem?.type === 'call' ? selectedCallId : null,
    { refetchInterval: 10000 },
  );

  // The Human Call Script binds to the LIVE call (the one created on dial = the latest call),
  // independent of the user's selected call — so the Review tab's selection and behaviour are
  // never affected. Fetch the latest call's full record only while the live script tab is shown
  // (React Query dedupes this with the selected fetch above when they're the same call).
  const { data: liveHumanCallData } = useCall(caseId, liveCallId ?? null, {
    refetchInterval: 10000,
  });
  const liveHumanCallDetails = liveHumanCallData?.call;

  // Fetch the selected eligibility check details (only when an eligibility check is selected)
  const { data: selectedEligibilityCheckData, error: eligibilityCheckError } = useEligibilityCheck(
    caseId,
    selectedTabItem?.type === 'eligibility' ? selectedCallId : null,
    { refetchInterval: 10000 },
  );

  // Get call details when a call is selected (null when eligibility check is selected)
  const callDetails = selectedCallData?.call;

  // Whether the SELECTED call already has a saved disposition (ORs the numeric
  // `disposition_type` and the legacy label-only `disposition.type`). This is what closes
  // the script's edit window, so it follows the call being written up — the selection —
  // rather than the live call, which is gone by the time the write-up happens.
  const selectedCallHasSavedDisposition = !!callDetails && hasCallDisposition(callDetails);

  const { humanCallEditable, humanCallActive } = useHumanCallEditState({
    callScriptAvailable,
    // Live call: drives select-on-dial and moveToCallRecording only.
    callDetails: liveHumanCallDetails,
    // Selected call: what the script reads, writes and gates editability on.
    selectedCallDetails: callDetails,
    hasSavedCallDisposition: selectedCallHasSavedDisposition,
    caseDetails,
    isCurrentUserAssigned,
    setActiveTab,
    setSelectedCallId,
    setManuallySelectedCallId,
    latestCallId,
  });

  // Build review tab extracted metadata with current callId injected as the
  // field-level source identifier. Lets the "View evidence" button work in
  // the Review tab where the call's own metadata doesn't carry a cross-
  // reference (the source IS the call itself). Preserves any pre-existing
  // source_id; supports legacy source_call_id on unrenamed extracted_data rows.
  const reviewExtractedMetadata = useMemo(() => {
    const meta = callDetails?.extracted_data?.output?.metadata as
      | { fields?: Record<string, any> }
      | undefined;
    if (!meta?.fields || !callDetails?.id) return meta;
    const fields: Record<string, any> = {};
    for (const [key, value] of Object.entries(meta.fields)) {
      const fieldMeta = value as any;
      const existingId =
        (fieldMeta?.source_type === 'call' && fieldMeta?.source_id) || fieldMeta?.source_call_id;
      fields[key] = {
        ...fieldMeta,
        source_id: existingId || callDetails.id,
        source_type: 'call',
      };
    }
    return { ...meta, fields };
  }, [callDetails?.extracted_data?.output?.metadata, callDetails?.id]);

  // Combine all error sources
  const error =
    callsError || callError || caseError || eligibilityChecksError || eligibilityCheckError;

  // Calculate error button config once
  const errorButtonConfig = useMemo(
    () => (error ? getErrorButtonConfig(error?.APIHttpStatus, navigate) : null),
    [error, navigate],
  );

  // Get the appropriate input/output data based on the selected tab type
  // Separates embedded metadata from output_data so it doesn't render as a field
  const displayData = useMemo(() => {
    const separateMetadata = (raw: any) => {
      if (!raw) return { data: raw, metadata: undefined };

      const { __metadata__: metadata, ...data } = raw;
      return { data: Object.keys(data).length > 0 ? data : raw, metadata };
    };

    if (!selectedTabItem) {
      const rawOutput = caseDetails?.output_data?.data || caseDetails?.output_data;
      const { data: outputData, metadata: outputMetadata } = separateMetadata(rawOutput);
      return {
        inputData: caseDetails?.input_data?.data || caseDetails?.input_data,
        outputData,
        outputMetadata,
        type: 'case' as const,
      };
    }

    if (selectedTabItem.type === 'eligibility') {
      const eligibilityCheck = selectedEligibilityCheckData?.eligibility_check;
      return {
        inputData: eligibilityCheck?.input_data,
        outputData: eligibilityCheck?.output_data,
        outputMetadata: undefined,
        type: 'eligibility' as const,
      };
    } else if (selectedTabItem.type === 'call') {
      const rawOutput = callDetails?.output_data?.data || callDetails?.output_data;
      const { data: outputData, metadata: outputMetadata } = separateMetadata(rawOutput);
      return {
        inputData: callDetails?.input_data?.data || callDetails?.input_data,
        outputData,
        outputMetadata,
        type: 'call' as const,
      };
    } else if (selectedTabItem.type === 'ai_audit' || selectedTabItem.type === 'human_audit') {
      // AI/Human Audit reuses the case's input/output; the audit row supplies
      // corrections/feedback on top of it, surfaced via correctionsByField.
      const rawOutput = caseDetails?.output_data?.data || caseDetails?.output_data;
      const { data: outputData, metadata: outputMetadata } = separateMetadata(rawOutput);
      return {
        inputData: caseDetails?.input_data?.data || caseDetails?.input_data,
        outputData,
        outputMetadata,
        type: 'ai_audit' as const,
      };
    } else {
      const rawOutput = caseDetails?.output_data?.data || caseDetails?.output_data;
      const { data: outputData, metadata: outputMetadata } = separateMetadata(rawOutput);
      return {
        inputData: caseDetails?.input_data?.data || caseDetails?.input_data,
        outputData,
        outputMetadata,
        type: 'case' as const,
      };
    }
  }, [selectedTabItem, caseDetails, callDetails, selectedEligibilityCheckData]);

  // Output data metadata (evidence for output_data fields only, NOT merged with extracted_data).
  //
  // For the call tab, the source identifier is *unconditionally* set to the
  // current call's id. This is asymmetric with reviewExtractedMetadata above,
  // which preserves any pre-existing source_id — and that asymmetry is
  // intentional: the call tab renders the call's *own* output_data, which by
  // definition was produced by this call. Any pre-existing source_id on the
  // raw call.output_data.metadata is either stale (older write) or wrong
  // (cross-referenced from elsewhere) and should be overwritten.
  // reviewExtractedMetadata, by contrast, renders extracted_data which is
  // merged across calls — there each field's source_id legitimately belongs
  // to whichever call contributed it, so we must preserve it.
  const outputDataMetadata = useMemo(() => {
    const meta = displayData.outputMetadata as { fields?: Record<string, any> } | undefined;
    if (!meta?.fields) return undefined;
    if (displayData.type === 'call' && callDetails?.id) {
      // Shape-preserving: an entity field's metadata is an ARRAY (one entry per
      // drug/procedure), and spreading it here collapsed it to numeric keys, which
      // cost every nested leaf its evidence and chain of thought (LA-6912).
      const fields: Record<string, any> = {};
      for (const [key, value] of Object.entries(meta.fields)) {
        fields[key] = stampCallSource(value, callDetails.id);
      }
      return { ...meta, fields };
    }
    return meta;
  }, [displayData.outputMetadata, displayData.type, callDetails?.id]);

  // The PRE-audit value of one root output field — the "before" side of every audit diff.
  // Hoisted out of the auditor panel's JSX because the changed-field count needs exactly
  // the same values the rows are rendered from; a count computed off different data would
  // disagree with what the panel shows.
  const auditFieldValue = useCallback(
    (fieldPath: string): unknown =>
      resolveAuditBeforeValue(fieldPath, {
        snapshot: selectedAudit?.output_snapshot as Record<string, unknown> | undefined,
        isHumanAudit: selectedAudit?.type === AuditType.AUDIT_TYPE_HUMAN,
        correction: correctionsByField[fieldPath],
        preAuditCallOutput,
        currentOutput: displayData.outputData as Record<string, unknown> | undefined,
      }),
    [
      selectedAudit?.type,
      selectedAudit?.output_snapshot,
      correctionsByField,
      displayData.outputData,
      preAuditCallOutput,
    ],
  );

  // Number of FIELDS the audit changed, which is what the "Changed fields" pill reports.
  // A leaf correction is one field; a root-level correction on a container field carries
  // the whole before/after structure, so it counts the leaves that actually differ —
  // a `drug` CHANGE that touched three leaves reads as 3, not 1.
  const changedFieldCount = useMemo(
    () =>
      changedCorrections.reduce(
        (total, row) =>
          total +
          (row.leafPath ? 1 : countChangedLeaves(auditFieldValue(row.rootId), row.correctedValue)),
        0,
      ),
    [changedCorrections, auditFieldValue],
  );

  // Revert one audited field to the value it held before the audit (BV-1951). Writes the
  // single path against the CASE output — the audit's own record is history and is left
  // untouched, so the row keeps showing what the audit did. Offered only where the viewer
  // may edit the output; the panel hides the affordance entirely when this is undefined.
  const updateCaseOutput = useUpdateCaseOutput();

  // Field paths already reverted, keyed `<auditId>::<fieldPath>`. Owned here rather than
  // inside the Revert control because that control unmounts with the audit panel: leaving
  // the tab and coming back used to re-offer Revert on a field already written back. Keyed
  // by audit so the acknowledgement follows the audit it belongs to and never leaks onto
  // another audit's row for the same field.
  const [revertedAuditFields, setRevertedAuditFields] = useState<Set<string>>(
    () => new Set<string>(),
  );
  const isAuditFieldReverted = useCallback(
    (fieldPath: string) => revertedAuditFields.has(`${selectedAudit?.id ?? ''}::${fieldPath}`),
    [revertedAuditFields, selectedAudit?.id],
  );

  // Reverts run strictly one after another (LA-7951). Each one rebuilds a whole top-level
  // field from the case output and the server replaces arrays wholesale, so two reverts
  // in flight at once — a rep clicking down the list a second apart — each built on a copy
  // that lacked the other, and the later one silently undid the earlier.
  const revertChainRef = useRef<Promise<void>>(Promise.resolve());

  const revertAuditField = useCallback(
    (fieldPath: string, value: unknown): Promise<void> => {
      const run = async () => {
        if (!caseId) return;
        const caseKey = queryKeys.cases.byId(caseId);
        // Base the write on the case as the SERVER now holds it — after every earlier
        // revert in the chain has landed — not on this render's copy, which a poll or a
        // not-yet-applied refetch can leave behind.
        await queryClient.refetchQueries({ queryKey: caseKey, exact: true });
        const latest = queryClient.getQueryData<typeof caseDetails>(caseKey) ?? caseDetails;
        const base = (latest?.output_data?.data ?? {}) as Record<string, unknown>;
        // `undefined` would be dropped by JSON serialization, and UpdateOutputData merges
        // only the keys it receives — so a field the audit ADDED would silently keep the
        // audited value. Send an explicit null to clear it.
        const next = setOutputAtPath(base, fieldPath, value === undefined ? null : value);
        // Send only the top-level field this revert touches. The server merges, so every
        // other field is left exactly as it is instead of being re-sent from our copy.
        const rootKey = outputPathRootKey(fieldPath);
        try {
          await updateCaseOutput.mutateAsync({
            caseId,
            outputData: {
              schema_version: latest?.output_data?.schema_version ?? '1.0',
              data: { [rootKey]: next[rootKey] },
            },
          });
          // Only after the write lands — a failed revert must stay revertable for a retry.
          const auditId = selectedAudit?.id ?? '';
          setRevertedAuditFields(prev => new Set(prev).add(`${auditId}::${fieldPath}`));
          toast.success('Reverted to the value before the audit');
        } catch (error) {
          toast.error(
            error instanceof Error ? error.message : 'Could not revert the field — please retry',
          );
          throw error;
        }
      };
      const result = revertChainRef.current.then(run, run);
      // Keep the chain alive past a failed revert; the caller still sees the rejection.
      revertChainRef.current = result.catch(() => undefined);
      return result;
    },
    [caseId, caseDetails, queryClient, selectedAudit?.id, updateCaseOutput],
  );

  // Call start timestamp in ms — used to resolve heard marker offsets.
  const callStartedMs = useMemo(() => {
    const t = callDetails?.started_at;
    const ms = t ? Date.parse(t) : NaN;
    return Number.isNaN(ms) ? undefined : ms;
  }, [callDetails?.started_at]);

  // Merge heard markers from the call into the output metadata as per-field heardSeek
  // nodes, so the dedicated HeardSeekButton renders an affordance that seeks the recording.
  const outputMetadataWithHeard = useMemo(() => {
    if (displayData.type !== 'call' || !callDetails?.id) return outputDataMetadata;
    // No heard markers (the common case — only human-call cases ever set them) → skip
    // buildHeardSeek + the mergeHeardSeek deep-clone entirely and return the metadata
    // unchanged, so background polls (which churn heard_markers/transcriptSegments) don't
    // rebuild the whole tree and re-render the large Review accordions every tick.
    const markers = callDetails.heard_markers;
    if (!markers || Object.keys(markers).length === 0) return outputDataMetadata;
    const heard = buildHeardSeek(markers, transcriptSegments, callStartedMs, callDetails.id);
    return mergeHeardSeek(outputDataMetadata as FieldMetadata | undefined, heard);
  }, [
    outputDataMetadata,
    displayData.type,
    callDetails?.id,
    callDetails?.heard_markers,
    transcriptSegments,
    callStartedMs,
  ]);

  // Fetch audio assets for the selected call (only if it's actually a call, not an eligibility check)
  const { data: audioAssets = [] } = useCallAssets(
    caseId,
    displayData.type === 'call' ? selectedCallId : null,
    AssetType.ASSET_TYPE_CALL_FULL_RECORDING,
    {
      pollingInterval: 5000,
      stopPollingWhenFound: true,
      requireUploadComplete: true,
    },
  );

  // Fetch transcript assets for the selected call (only if it's actually a call, not an eligibility check)
  const { data: transcriptAssets = [], isLoading: transcriptAssetsLoading } = useCallAssets(
    caseId,
    displayData.type === 'call' ? selectedCallId : null,
    AssetType.ASSET_TYPE_CALL_TRANSCRIPT,
    {
      pollingInterval: 5000,
      stopPollingWhenFound: true,
    },
  );

  // Fetch document assets at the case record level (not tied to specific call)
  const { data: documentAssets = [] } = useCaseRecordAssets(
    caseId,
    AssetType.ASSET_TYPE_CALL_DOCUMENT,
  );

  // Check if we have assets
  const hasAudioAsset = audioAssets.length > 0;
  const hasDocumentAsset = documentAssets.length > 0;

  // Get the document asset (only one document per case)
  const documentAsset = documentAssets[0];
  const documentAssetId = useMemo(() => {
    return documentAsset?.id || null;
  }, [documentAsset]);

  // Construct output data structure - ALWAYS use schema as base, merge in API data
  // This ensures all schema fields are present for proper change detection
  const outputData = useMemo(() => {
    // Start with schema-based empty structure (has all expected fields)
    const schemaData = constructEmptyDataFromGroups(outputGroups);

    // Merge in actual values from API if they exist
    if (displayData.outputData) {
      return mergeExtractedData(schemaData, displayData.outputData);
    }

    return schemaData;
  }, [displayData.outputData, outputGroups]);

  // Restores the case-level disposition_code write GlobalDialpad's post-call
  // Save used to make alongside the call's own disposition (BI-1848 review
  // finding) — the cases-table column, CSV export, workforce card, and
  // caserecord.Filter("DispositionCode") all read this field.
  const updateDispositionCode = useUpdateDispositionCode(caseId);

  // The disposition catalog for this case's workflow, used to resolve a
  // numeric disposition code chosen in the "Confirm Action" dialog into the
  // {label, output_data_flag} pair `buildDispositionPayload` needs (BI-1848).
  // Unconditional on `selectedWorkflow?.workflow_type` — unlike
  // `callDispositions` below, which is scoped to the currently-selected human
  // call — because this write targets the case's LATEST human call
  // (`latestHumanCall`), whatever call happens to be selected in Review.
  // `useDispositions` is a pure, synchronous derivation (no network call), so
  // a second call with the same workflow_type costs nothing.
  const { data: dispositionsForCaseWorkflow = [] } = useDispositions(
    selectedWorkflow?.workflow_type,
  );

  // Completion guard: the human-call step may not be marked completed while
  // that same latest human call carries no disposition. The server enforces
  // the same rule in executeHumanCallAction; this gates the Confirm dialog up
  // front so the agent is told before clicking, not by a rejection toast.
  //
  // Declared here, not beside newCallBlockReason, because it needs
  // dispositionsForCaseWorkflow above: the rule only applies to workflows the
  // disposition engine has a catalog for.
  const humanCallCompletionBlockReason = useMemo(
    () =>
      getHumanCallCompletionBlockReason(latestHumanCall, dispositionsForCaseWorkflow.length > 0),
    [latestHumanCall, dispositionsForCaseWorkflow],
  );

  // Whether the case's latest human call carries no disposition of its own.
  //
  // Deliberately NOT the same signal as humanCallCompletionBlockReason above.
  // That one mirrors the server guard, which skips calls that never became a
  // conversation (not_started — the status EVERY human call is created with,
  // see service/calls.go CreateCall — plus scheduled/cancelled) so a phantom
  // row can never wedge a case. A call the agent dialled from an external
  // handset, or one whose terminal-status webhook never landed, is still
  // sitting at not_started, so the guard stays silent for it.
  //
  // The Confirm dialog needs the weaker fact, because its failure mode is not a
  // wedge but a SILENT one: its required "Case Disposition" selector falls back
  // to the case's own disposition_code, which ACTION_CALCULATE_DISPOSITION has
  // usually just auto-populated when the agent left the Human Call tab. Confirm
  // then sits enabled with no interaction, and since the call-level write below
  // only fires on a genuine pick, the call is routed to human audit with no
  // disposition at all. Forcing a real pick here costs one click and cannot
  // wedge anything — the selector that clears it is in the same dialog.
  const latestHumanCallMissingDisposition = useMemo(
    () => !!latestHumanCall && !hasCallDisposition(latestHumanCall),
    [latestHumanCall],
  );

  // Dedicated mutation for the call-level disposition write below — separate
  // from useCallDataEditor's internal instance, which is bound to whichever
  // call is selected in Review, not necessarily the latest human call.
  const updateLatestHumanCallDisposition = useUpdateCall();

  // Also write the case's latest human call's own disposition/disposition_type
  // when an agent picks a disposition in the Confirm dialog for finishing a
  // human call (BI-1848) — mirroring what the Output section's own Save
  // writes for the call, via the same buildDispositionPayload shape. Scoped
  // to the two human_call_* actions by the caller below: the four
  // pre-existing close-case actions (mark_as_completed/mark_as_terminated/
  // complete/terminate) fire from human_audit, where there is no relevant
  // call, and must not gain this write.
  //
  // Returns a promise the caller MUST await before firing the action: the
  // server's completion guard reads the call's disposition, so an
  // ExecuteAction that overtakes this PATCH would be rejected.
  const writeLatestHumanCallDisposition = useCallback(
    async (dispositionCode: number) => {
      if (!caseId || !latestHumanCall?.id) return;
      const matched = dispositionsForCaseWorkflow.find(d => d.code === dispositionCode);
      // A code this workflow's engine catalog cannot resolve must never be
      // written. `buildDispositionPayload` turns an unmatched code into
      // `type: ''`, which persists as a disposition that every guard counts as
      // SET (callHasDisposition is `disposition_type != 0`, client and server)
      // while the UI renders it blank — strictly worse than leaving the call
      // undispositioned, and unfixable by the guards afterwards. This is
      // reachable whenever a stale pre-engine `disposition_code` rides in from
      // the case (e.g. 104, written by case_sync off a failed AI call, absent
      // from every 1_0xx_xxx catalog). Throwing aborts the action too — the
      // caller toasts and does not route the case.
      if (!matched?.label) {
        throw new Error(`disposition code ${dispositionCode} is not in this workflow's catalog`);
      }
      const disposition: DispositionEdit = {
        type: matched.label,
        dispositionType: dispositionCode,
        outputDataFlag: matched.output_data_flag ?? null,
      };
      const payload = buildDispositionPayload(disposition, latestHumanCall.output_data, {
        savedNotes: latestHumanCall.disposition?.notes,
        workflowType: selectedWorkflow?.workflow_type,
        // Always a genuine pick from the Confirm dialog's selector, never a
        // notes edit — so the output_data flag reset always applies.
        dispositionTypeChanged: true,
      });
      await updateLatestHumanCallDisposition.mutateAsync({
        caseRecordId: caseId,
        callId: latestHumanCall.id,
        data: payload,
      });
    },
    [
      caseId,
      latestHumanCall,
      dispositionsForCaseWorkflow,
      updateLatestHumanCallDisposition,
      selectedWorkflow?.workflow_type,
    ],
  );

  // Whether the Output section may be edited right now — the single source of
  // truth for the EditableSectionHeader's `canEdit` AND for gating
  // `useCallDataEditor`'s `editingCallSections.output` (BI-1848). Computed
  // once, here, rather than inline on the header only: a stale localStorage
  // draft written while the case was editable (e.g. human_call) must not
  // force the section into edit mode after it moves somewhere editing is
  // locked (e.g. human_audit), and duplicating this expression in two places
  // is exactly how that regression would silently come back.
  //
  // Editable only while the case is in a human call (LA-8051), the same rule
  // UpdateCall enforces server-side. Outside it the call's output is mirrored
  // onto a case that may refuse it (completed, for one), so the two would
  // silently diverge; the server now rejects the write instead.
  const canEditOutput =
    !!selectedCallId &&
    selectedCallId === latestCallId &&
    displayData.type === 'call' &&
    callDetails?.type === CallType.CALL_TYPE_HUMAN &&
    isCurrentUserAssigned(caseDetails?.assigned, caseDetails?.assigned_user_id) &&
    isHumanCallState(caseDetails?.state);

  // Use the call data editor hook for managing input/output editing
  const {
    editingCallSections,
    editedCallData,
    isSaving,
    handleCallEditStart,
    handleCallEditCancel,
    handleCallSave,
    handleDataChange,
    handleDispositionChange,
  } = useCallDataEditor({
    caseId: caseId || undefined,
    selectedCallId:
      selectedCallId && selectedCallId !== PENDING_HUMAN_CALL_TAB_ID ? selectedCallId : undefined,
    callDetails,
    outputData,
    canEdit: canEditOutput,
    workflowType: selectedWorkflow?.workflow_type,
    onDispositionSaved: useCallback(
      (dispositionCode: number) => updateDispositionCode.mutate(dispositionCode),
      [updateDispositionCode],
    ),
  });

  // Per-field validation errors for the editable output board (keyed by field
  // id). Populated only when the user clicks Save; phone fields are checked
  // against their country code's expected length via libphonenumber.
  const [outputFieldErrors, setOutputFieldErrors] = useState<Record<string, string>>({});

  /**
   * The Call Script layout's live validation errors, mirrored up from the board (LA-7358).
   *
   * That layout writes into the WORKSPACE's draft, not this component's, so
   * `outputFieldErrors` above is always empty there — which meant a Save refused by
   * validation showed no "Validation errors found." banner at all, only the inline
   * message on the row. This is the same signal, in this component's hands, so the
   * banner and its View action work in both layouts.
   */
  const [scriptOutputErrors, setScriptOutputErrors] = useState<Record<string, string>>({});

  // Scroll the output board to the first field with a validation error (schema
  // order). Expands all groups first so a field inside a collapsed accordion is
  // reachable, then scrolls after the DOM has had a tick to render. Mirrors
  // CaseSummaryView.scrollToFirstOutputError.
  // Imperative handle on the Call Script layout's Output board, so the error jump can
  // reach a field the board is currently hiding (BV-2129).
  const reviewOutputBoardRef = useRef<ReviewOutputBoardHandle | null>(null);

  const scrollToFirstOutputError = useCallback(
    (errors: Record<string, string>) => {
      // collectPhoneFieldErrors inserts keys in schema document order, so the
      // first key is the first failing field (works for nested dot-paths too).
      const firstErrorId = Object.keys(errors)[0];
      if (!firstErrorId) return;

      // Call Script layout (BV-1575): the pane is the field board, not the accordion, so
      // none of the accordion choreography below applies — expanding `outputGroups` does
      // nothing, and the row may be filtered out, searched away or in a collapsed
      // section, i.e. not in the DOM to query at all. The board knows how to undo all
      // three, so ask it (BV-2129). Without this the error message appeared and the jump
      // silently did nothing.
      //
      // Keyed on the ref being attached rather than on `showReviewCallScript`, which is
      // declared further down: the board is the only thing that sets this ref, so a
      // non-null handle IS "the Call Script layout is mounted" — and it cannot go stale
      // the way a captured boolean could.
      if (reviewOutputBoardRef.current) {
        reviewOutputBoardRef.current.revealField(firstErrorId);
        return;
      }

      // Open all accordions first, then wait for them to fully expand/render
      // before scrolling — scrolling too early (while a group is still
      // collapsed) lands on the wrong position or fails to find the field.
      setReviewOutputExpanded(outputGroups.map((_, index) => index.toString()));
      setTimeout(() => {
        const root = outputFieldsRef.current ?? document;
        const el = root.querySelector(`[data-field-id="${firstErrorId}"]`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 500);
    },
    [outputGroups],
  );

  // Validate phone fields on save; block the save and surface errors when any
  // fail. Clears prior errors on a clean run.
  const handleOutputSaveClick = useCallback(() => {
    // Call Script layout: the board and script write into the WORKSPACE's
    // useCallDataEditor, a different useState draft from this component's — they share no
    // backing store, since the human call opts out of localStorage for PHI. Validating
    // `editedCallData.output` here therefore inspected a copy that never saw those edits
    // and always came back clean, so Save neither reported nor navigated to anything
    // (BV-2129). The board validates its own draft and reveals the first failure.
    const scriptBoard = reviewOutputBoardRef.current;
    if (scriptBoard) {
      if (scriptBoard.validateAndReveal()) return;
      // Same split, for the write (BV-2171): this component's output draft is the snapshot
      // taken when Edit was pressed, so saving it reverted every field the rep captured in
      // the script since. Save writes only the disposition; its output flags go to the
      // workspace, which owns — and autosaves — the output.
      handleCallSave({ applyOutputFlags: scriptBoard.applyOutputPatch });
      return;
    }

    const errors = collectPhoneFieldErrors(outputGroups, editedCallData.output);
    setOutputFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      scrollToFirstOutputError(errors);
      return;
    }
    handleCallSave();
  }, [outputGroups, editedCallData.output, handleCallSave, scrollToFirstOutputError]);

  /**
   * Gate for the Confirm dialog's Case Disposition picker (LA-7358).
   *
   * Same validation `handleOutputSaveClick` runs, at the other place a rep commits
   * their work: confirming writes the disposition and routes the case on, after which
   * fixing a malformed number means reopening a case that has already moved state.
   *
   * Two sources, because the two layouts keep their working output in different
   * `useCallDataEditor` instances (see `handleOutputSaveClick`): the Call Script
   * board validates and reveals its own draft, and the accordion layout validates
   * this component's. Returns the dialog's message, or null to let Confirm proceed.
   */
  const validateOutputBeforeConfirm = useCallback((): string | null => {
    const message = 'Fix the highlighted validation errors in the output before continuing.';
    // Also reveals the first failing row behind the dialog, so dismissing it lands
    // the rep on the field to fix.
    if (reviewOutputBoardRef.current) {
      return reviewOutputBoardRef.current.validateAndReveal() ? message : null;
    }
    const errors = collectPhoneFieldErrors(outputGroups, editedCallData.output);
    setOutputFieldErrors(errors);
    if (Object.keys(errors).length === 0) return null;
    scrollToFirstOutputError(errors);
    return message;
  }, [outputGroups, editedCallData.output, scrollToFirstOutputError]);

  // Live phone/fax validation: re-check the edited field on every change so the
  // error shows immediately and clears as soon as the number becomes valid —
  // without requiring another save attempt. Mirrors CaseSummaryView.
  const handleOutputDataChange = useCallback(
    (updatedData: any, fieldPath?: string) => {
      handleDataChange('output', updatedData);
      if (!fieldPath) return;
      const error = getPhoneFieldError(
        outputGroups,
        fieldPath,
        getNestedValue(updatedData, fieldPath),
      );
      setOutputFieldErrors(prev => {
        if (!error && !prev[fieldPath]) return prev;
        const next = { ...prev };
        if (error) next[fieldPath] = error;
        else delete next[fieldPath];
        return next;
      });
    },
    [handleDataChange, outputGroups],
  );

  // Header message shown in red below the output Save button. Matches the
  // copy used on the case summary output board.
  //
  // Reads BOTH stores (LA-7358): the accordion layout populates `outputFieldErrors`,
  // the Call Script layout mirrors the workspace's into `scriptOutputErrors`. Keying
  // off the first alone left the script layout with a Save that refused silently.
  const outputSaveError =
    Object.keys(outputFieldErrors).length > 0 || Object.keys(scriptOutputErrors).length > 0
      ? 'Validation errors found.'
      : undefined;

  /**
   * Jump to the first failing field, whichever layout is mounted (LA-7358).
   *
   * The script layout's failures are keyed by the board's own row keys and live behind
   * its filter/search/collapse state, so only the board can find them — hence
   * `validateAndReveal` rather than this component's `scrollToFirstOutputError`, which
   * queries `data-field-id` against the accordion.
   */
  const revealFirstOutputError = useCallback(() => {
    if (reviewOutputBoardRef.current) {
      reviewOutputBoardRef.current.validateAndReveal();
      return;
    }
    scrollToFirstOutputError(outputFieldErrors);
  }, [outputFieldErrors, scrollToFirstOutputError]);

  // Dispositions for the human call output section dropdown.
  const isHumanCallSelected =
    displayData.type === 'call' && callDetails?.type === CallType.CALL_TYPE_HUMAN;

  // --- Call Script in the Review view (BV-1575, scoped by BV-2159) --------
  // Gated on the case being in a human-call state AND the right thing being selected in
  // the attempts strip. Exactly two selections get the script column:
  //
  //  - the pending-attempt pill, i.e. pre-call: no call object exists yet, so the script
  //    reads the CASE's output, and
  //  - the human call of the CURRENT phase: live and for capture.
  //
  // "Current phase" is `hasHumanCallThisPhase`: the latest human call is still the newest
  // thing on the case. Once something newer exists (another call of any type, or an audit)
  // that call is spent history, and it drops to the untouched three-column Review along
  // with everything else — AI calls, texts, emails, eligibility checks, audits, and older
  // human calls.
  //
  // BV-2159: the script now hides the moment the case LEAVES `human_call`. It used to
  // stay up through the post-call write-up (into post_call_review / human_audit) so the
  // rep could finish beside the transcript — deliberately not state-gated. That is what
  // this reverts: once the call ends and the case advances out of human_call, the review
  // falls back to the normal transcript / input / output layout. The pending pill is
  // already state-scoped (its `callScriptAvailable` requires a human-call state), so this
  // state gate only newly bites the current-human-call selection during the write-up.
  const isCurrentHumanCallSelected =
    isHumanCallSelected &&
    !!selectedCallId &&
    selectedCallId === latestHumanCall?.id &&
    hasHumanCallThisPhase;
  // `!selectedCallId` covers the first render, before the strip's default selection lands
  // on the pending pill — without it the classic layout flashes for a frame.
  const isPendingHumanCallSelected =
    showPendingHumanCallItem && (!selectedCallId || selectedCallId === PENDING_HUMAN_CALL_TAB_ID);
  const wantsReviewCallScript =
    isHumanCallState(caseDetails?.state) &&
    (isPendingHumanCallSelected || isCurrentHumanCallSelected);
  // Per-workflow enablement stays data-driven (no env allowlist): a workflow with no
  // linked Call Script shows no column. Resolved only when a column could show, so it
  // costs nothing on other cases; React Query dedupes it with the phase gate's own lookup.
  const { data: reviewScriptData } = useActiveScript(
    wantsReviewCallScript ? caseDetails?.workflow_id : undefined,
  );
  const showReviewCallScript = wantsReviewCallScript && Boolean(reviewScriptData?.script);

  // Handoff actions for the CALLER, resolved as the script tab resolves them: from the most
  // recent audit that left any, rather than the selected audit (`auditorHandoffActions`,
  // which backs the AI-audit panel and follows the strip selection).
  const callerHandoffActions = useMemo(
    () => latestHandoffActions(caseAudit.audits ?? []),
    [caseAudit.audits],
  );
  // Which pane of the merged left column is showing. Opens on the auditor's handoff
  // actions — what the caller is meant to act on — with the transcript and inputs one click
  // away. Not every pane always exists: there is no handoff pane without actions, and no
  // transcript pane before a call is selected (pre-call there is no call object at all).
  // So the available set is derived and the effective tab falls back to the first one that
  // exists — which also keeps the view correct when a pane appears mid-session (audits are
  // polled; the transcript arrives when the call does).
  const reviewLeftTabs = useMemo<ReviewLeftTab[]>(
    () => [
      ...(callerHandoffActions.length > 0 ? (['handoff'] as const) : []),
      ...(displayData.type === 'call' && selectedCallId ? (['transcript'] as const) : []),
      'input',
    ],
    [callerHandoffActions.length, displayData.type, selectedCallId],
  );
  const effectiveReviewLeftTab = reviewLeftTabs.includes(reviewLeftTab)
    ? reviewLeftTab
    : reviewLeftTabs[0];
  // The disposition lives in the Output panel, beside the fields it is derived
  // from. It is always surfaced for a human call whose workflow has a
  // disposition catalog; edit mode decides whether it is live, not whether the
  // call has been disposed already.

  // Disposition helper — runs the disposition engine against committed output
  // fields to surface suggested disposition + the next field to ask.
  // Visible whenever this is a human call (not gated on edit mode); during edit
  // mode it snapshots only on field commit (blur for text inputs; selects/dates
  // already commit immediately on change inside FieldGroupAccordion).
  // The commit-gated snapshot + engine wiring lives in `useCommittedDisposition`,
  // shared with the Human Call tab. During edit mode the snapshot advances only on
  // field commit (blur for text inputs; selects/dates commit immediately on change
  // inside FieldGroupAccordion); when not editing it tracks the server baseline.
  // The engine result is preferred for badges/section scores when the workflow
  // type is supported; otherwise we fall back to the persisted call disposition
  // (AI calls and unsupported agents carry `call.disposition` directly). These
  // drive the All/Missed/Required tabs and the group filter.

  // While editing a human call's output, surface the AI-extracted value as an
  // accept-able suggestion chip on each scalar input (reuses InputWithSuggestion
  // via ScalarFieldEditable). Sourced from the call's extracted_data; Accept fills
  // the field. Output editing is already gated to human calls, so the chip only
  // appears there.
  const [dismissedCallSuggestions, setDismissedCallSuggestions] = useState<Set<string>>(new Set());
  // Reset dismissals when the selected call changes or an edit session toggles.
  useEffect(() => {
    setDismissedCallSuggestions(new Set());
  }, [callDetails?.id, editingCallSections.output]);

  const humanCallSuggestionProps: GetFieldSuggestionProps = useCallback(
    fieldId => {
      // fieldId is a full dot-path: flat for top-level scalars ("coverage_status")
      // and nested for array/object leaves ("drug.0.coverage.coverage_status").
      const getByPath = (obj: unknown, path: string): unknown =>
        path
          .split('.')
          .reduce<unknown>(
            (acc, key) => (acc == null ? undefined : (acc as Record<string, unknown>)[key]),
            obj,
          );
      const rawSuggestion = getByPath(callDetails?.extracted_data?.output?.data, fieldId);
      const current = getByPath(editedCallData.output, fieldId);
      const dismissed = dismissedCallSuggestions.has(fieldId);
      // Show only when there's an extracted value that differs from the current edit.
      const hasSuggestion =
        !dismissed &&
        rawSuggestion !== undefined &&
        rawSuggestion !== null &&
        String(rawSuggestion) !== String(current ?? '');
      // Per-field extraction evidence powers the chip's Review button + reasoning
      // lightbulb. reviewExtractedMetadata.fields mirrors the data tree (leaves are
      // {evidence}), so the same dot-path walk resolves both scalar and nested
      // fields. The suggestion comes from the call being edited, so its evidence
      // anchors to callDetails.id.
      const evidenceLeaf = getByPath(reviewExtractedMetadata?.fields, fieldId) as
        | { evidence?: FieldEvidence[] }
        | undefined;
      return {
        suggestion: hasSuggestion ? rawSuggestion : undefined,
        hint: undefined,
        isCollapsed: false,
        evidence: hasSuggestion ? evidenceLeaf?.evidence : undefined,
        sourceCallId: callDetails?.id,
        onNavigateToCall: handleNavigateToCall,
        onNavigateToAudit: handleNavigateToAudit,
        onAccept: () => setDismissedCallSuggestions(prev => new Set(prev).add(fieldId)),
        onReject: () => setDismissedCallSuggestions(prev => new Set(prev).add(fieldId)),
        onExpand: () =>
          setDismissedCallSuggestions(prev => {
            const next = new Set(prev);
            next.delete(fieldId);
            return next;
          }),
      };
    },
    [
      callDetails?.extracted_data,
      callDetails?.id,
      editedCallData.output,
      dismissedCallSuggestions,
      reviewExtractedMetadata,
      handleNavigateToCall,
      handleNavigateToAudit,
    ],
  );
  // Passing a workflow type is what switches the disposition engine ON, and with it the
  // Output panel's All/Missed/Required pills and its per-field required/missing marking.
  // The pending-attempt selection needs both: pre-call there is no call object, but the
  // rep is looking at the same output they are about to fill, so what is still required or
  // missed is exactly what they want to see. It runs against the CASE's output there
  // (`outputData` follows the selection), which is the right baseline before a call.
  const dispositionHelperWorkflowType =
    isHumanCallSelected || isPendingHumanCallSelected ? selectedWorkflow?.workflow_type : undefined;
  // Keyed on the CASE's workflow (not `selectedWorkflow`) to mirror bvagent's resolution.
  const engineAgentId = useEngineAgentId(caseDetails?.workflow_id);
  const {
    committedOutput,
    handleOutputCommit,
    helper: dispositionHelper,
    requiredFields: activeRequiredFields,
    missingFields: activeMissingFields,
    dispositionFields: outputDispositionFields,
  } = useCommittedDisposition({
    workflowType: dispositionHelperWorkflowType,
    baselineOutput: outputData,
    editedOutput: editedCallData.output,
    isEditing: editingCallSections.output,
    fallbackDisposition: callDetails?.disposition,
    // Combined engine context: case input (seed) overlaid with committed output.
    inputData: caseDetails?.input_data?.data as Record<string, unknown> | undefined,
    // The engine resolves program-flag-gated required fields solely from the case
    // workflow's agent id (BI-1795).
    agentId: engineAgentId,
  });
  // Build labels for chips by walking the output schema recursively. The engine
  // returns paths like `drug[0].coverage.coverage_status` for VIIV MedBV's
  // nested format; we emit the same path style here so the chip lookup hits.
  // Format: "{topLevelLabel} — {leafLabel}" for nested leaves; just the field
  // label for top-level scalars.
  const outputFieldLabels = useMemo(() => {
    const map: Record<string, string> = {};
    type FieldLike = {
      id?: string;
      label?: string;
      type?: string;
      fields?: FieldLike[];
    };
    const walk = (field: FieldLike, basePath: string, topLevelLabel: string) => {
      if (!field.id) return;
      const isArray = field.type === 'array';
      const isObject = field.type === 'object';
      const segment = isArray ? `${field.id}[0]` : field.id;
      const path = basePath ? `${basePath}.${segment}` : segment;
      const childTopLabel = topLevelLabel || field.label || field.id;
      if (isArray || isObject) {
        field.fields?.forEach(sub => walk(sub, path, childTopLabel));
        return;
      }
      // Scalar leaf
      const leafLabel = field.label ?? field.id;
      const label =
        topLevelLabel && topLevelLabel !== leafLabel
          ? `${topLevelLabel} — ${leafLabel}`
          : leafLabel;
      map[path] = label;
    };
    outputGroups.forEach(group => {
      group.fields.forEach(field => walk(field as FieldLike, '', ''));
    });
    return map;
  }, [outputGroups]);

  // Per-parent child-index map for sorting Disposition Assist "Ask next"
  // chips by Nomos schema order. Built off the same outputGroups so it
  // stays in sync with the form labels above.
  const outputFieldOrder = useMemo(() => buildFieldSchemaOrder(outputGroups), [outputGroups]);

  // Pruned schema for the Output panel based on the active filter tab. Engine
  // returns paths like `drug[0].coverage.coverage_status`; the helper keeps an
  // ancestor only when a descendant survives.
  const filteredOutputGroups = useMemo(() => {
    if (outputFilter === OutputFilter.All) return outputGroups;
    const paths = outputFilter === OutputFilter.Missed ? activeMissingFields : activeRequiredFields;
    return filterOutputGroupsByPaths(outputGroups, new Set(paths));
  }, [outputFilter, outputGroups, activeMissingFields, activeRequiredFields]);

  // If the filter source has no required fields for the selected call (engine
  // unsupported AND call.disposition empty), the tabs hide — reset to All so a
  // later switch to a populated call starts on the default tab.
  useEffect(() => {
    if (activeRequiredFields.length === 0 && outputFilter !== OutputFilter.All) {
      setOutputFilter(OutputFilter.All);
    }
  }, [activeRequiredFields, outputFilter]);

  // Debug: log every disposition update with the snapshot + engine result.
  // Gated on NODE_ENV !== 'production' to honor the HIPAA rule that PHI
  // must not reach prod logs (output_data for a case can contain PHI).
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    if (!dispositionHelper.supported) return;
    // eslint-disable-next-line no-console
    console.log('[disposition assist]', {
      workflowType: dispositionHelperWorkflowType,
      data: committedOutput,
      disposition: dispositionHelper.current?.disposition,
      scenario: dispositionHelper.current?.matched_scenario,
      missingFields: dispositionHelper.missingFields,
      requiredFields: dispositionHelper.current?.required_params,
    });
  }, [dispositionHelper, committedOutput, dispositionHelperWorkflowType]);

  const outputFieldsRef = useRef<HTMLDivElement | null>(null);

  // Map a top-level field id (e.g. "drug") to the index of the output group
  // that contains it. Used when ask-next jumps to a nested field — we need to
  // open the right top-level accordion before walking deeper.
  const groupIndexByTopLevelFieldId = useMemo(() => {
    const map: Record<string, number> = {};
    outputGroups.forEach((group, idx) => {
      group.fields.forEach(f => {
        if (f.id) map[f.id] = idx;
      });
    });
    return map;
  }, [outputGroups]);

  const handleJumpToField = useCallback(
    (fieldKey: string) => {
      // Engine emits `drug[0].coverage.coverage_status`; the form's data-field-id
      // uses the dot-path form `drug.0.coverage.coverage_status`. Convert.
      const dotPath = fieldKey.replace(/\[(\d+)\]/g, '.$1');
      const root = outputFieldsRef.current;
      if (!root) return;

      // Top-level group: open via React state. The first dot-path segment is
      // the top-level field id (e.g. `drug`); look up which group owns it.
      const segments = dotPath.split('.');
      const topLevelFieldId = segments[0];
      const groupIdx = groupIndexByTopLevelFieldId[topLevelFieldId];
      if (typeof groupIdx === 'number') {
        const groupKey = String(groupIdx);
        setReviewOutputExpanded(prev => (prev.includes(groupKey) ? prev : [...prev, groupKey]));
      }

      // Ancestor accordion paths to open in order:
      //   `drug` (the per-field accordion in GroupFieldsRenderer)
      //   `drug.0` (array item)
      //   `drug.0.coverage` (sub-section inside the array item)
      // Skip the leaf itself.
      const ancestors: string[] = [];
      for (let i = 1; i <= segments.length - 1; i += 1) {
        ancestors.push(segments.slice(0, i).join('.'));
      }
      // Also include the bare top-level path so the per-field accordion opens
      // even when the leaf itself is at depth 1 (no-op for nested cases since
      // it's already the first ancestor).
      if (ancestors.length === 0) ancestors.push(topLevelFieldId);

      const MAX_RETRIES = 6;
      let cursor = 0;
      let retries = 0;

      const finish = () => {
        const fieldEl = root.querySelector(`[data-field-id="${dotPath}"]`) as HTMLElement | null;
        if (!fieldEl) return;
        fieldEl.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Brief warm-peach background flash that fades out — works uniformly
        // for every field type and avoids the blue-on-blue clash a ring would
        // create around our blue inputs. `bg-orange-*` is safelisted in
        // globals.css's @source inline list. `transition-colors duration-700`
        // is added first so removing `bg-orange-100` after the hold animates
        // back to the original background instead of snapping.
        fieldEl.setAttribute('data-jumped', 'true');
        fieldEl.classList.add('rounded', 'transition-colors', 'duration-700', 'bg-orange-100');
        window.setTimeout(() => {
          fieldEl.classList.remove('bg-orange-100');
        }, 900);
        window.setTimeout(() => {
          fieldEl.removeAttribute('data-jumped');
          fieldEl.classList.remove('rounded', 'transition-colors', 'duration-700');
        }, 1700);

        const input = fieldEl.querySelector(
          'input, textarea, select, button',
        ) as HTMLElement | null;
        input?.focus();
        // Auto-open Radix Popover-based triggers (SearchableSelect / DatePicker
        // / MultiSelect): they render as buttons with `role="combobox"` or
        // `aria-haspopup`. Click after focus so the popover opens.
        if (input instanceof HTMLButtonElement) {
          const isPopoverTrigger =
            input.getAttribute('role') === 'combobox' || input.hasAttribute('aria-haspopup');
          if (isPopoverTrigger) input.click();
        }
      };

      const step = () => {
        if (cursor >= ancestors.length) {
          finish();
          return;
        }
        const path = ancestors[cursor];
        const closed = root.querySelector(
          `[data-accordion-path="${path}"][data-state="closed"]`,
        ) as HTMLElement | null;
        const open = root.querySelector(
          `[data-accordion-path="${path}"][data-state="open"]`,
        ) as HTMLElement | null;
        if (open) {
          cursor += 1;
          retries = 0;
          step();
          return;
        }
        if (closed) {
          const trigger = closed.querySelector('button') as HTMLElement | null;
          trigger?.click();
          cursor += 1;
          retries = 0;
          window.requestAnimationFrame(step);
          return;
        }
        // Ancestor not yet in DOM (parent still rendering). Retry up to N
        // frames before giving up — covers the case where the top-level group
        // is opening on the next React commit.
        if (retries < MAX_RETRIES) {
          retries += 1;
          window.requestAnimationFrame(step);
          return;
        }
        finish();
      };

      // Defer to next frame so the React state update from
      // setReviewOutputExpanded above has been committed before we start
      // querying for nested accordions.
      window.requestAnimationFrame(step);
    },
    [groupIndexByTopLevelFieldId],
  );

  const { data: callDispositions = [], isLoading: isLoadingCallDispositions } = useDispositions(
    isHumanCallSelected ? selectedWorkflow?.workflow_type : undefined,
  );
  const useApiCallDispositions = callDispositions.length > 0;
  // Selectable-only list for the dropdown. `callDispositions` above stays the FULL
  // catalog because the label/code lookups below resolve through it — filtering that
  // array would break read-back for a call already set to a retired code. The current
  // value is kept selectable so the control is not blank.
  const { data: selectableCallDispositions = [] } = useSelectableDispositions(
    isHumanCallSelected ? selectedWorkflow?.workflow_type : undefined,
    callDetails?.disposition_type as number | undefined,
  );
  const callDispositionOptions = useMemo(
    () =>
      useApiCallDispositions
        ? selectableCallDispositions
            .filter(d => d.code !== undefined && d.label)
            .map(d => ({ value: d.code as number, label: d.label as string }))
        : [],
    [selectableCallDispositions, useApiCallDispositions],
  );

  const draftDisposition = editedCallData.disposition;
  const savedDispositionCode = callDetails?.disposition_type;
  const savedDispositionLabel = callDetails?.disposition?.type;
  const currentDispositionValue = useMemo<string | number | ''>(() => {
    const draftCode = draftDisposition?.dispositionType;
    const draftLabel = draftDisposition?.type;
    if (useApiCallDispositions) {
      if (typeof draftCode === 'number') return draftCode;
      if (typeof savedDispositionCode === 'number') return savedDispositionCode;
      // Older calls may carry only disposition.type (label) without a numeric
      // disposition_type — mirror GlobalDialpad's label→code lookup.
      const label = draftLabel ?? savedDispositionLabel;
      if (label) {
        const matched = callDispositions.find(d => d.label === label);
        if (typeof matched?.code === 'number') return matched.code;
      }
      return '';
    }
    return draftLabel ?? savedDispositionLabel ?? '';
  }, [
    useApiCallDispositions,
    draftDisposition,
    savedDispositionCode,
    savedDispositionLabel,
    callDispositions,
  ]);

  const handleCallDispositionSelect = useCallback(
    (value: number | '') => {
      if (value === '') {
        handleDispositionChange({
          type: undefined,
          dispositionType: undefined,
          outputDataFlag: null,
        });
        return;
      }
      const matched = callDispositions.find(d => d.code === value);
      handleDispositionChange({
        type: matched?.label,
        dispositionType: typeof value === 'number' ? value : undefined,
        outputDataFlag: matched?.output_data_flag ?? null,
      });
    },
    [callDispositions, handleDispositionChange],
  );

  // Map the live engine suggestion to a known disposition code by label so we
  // can wire it into the same select handler. If the suggestion doesn't match
  // any API option, we leave it undefined and skip the Accept button.
  const suggestedDisposition = dispositionHelper.current?.disposition;
  const suggestedDispositionCode = useMemo<number | undefined>(() => {
    if (!suggestedDisposition || !useApiCallDispositions) return undefined;
    const matched = callDispositions.find(d => d.label === suggestedDisposition);
    return typeof matched?.code === 'number' ? matched.code : undefined;
  }, [suggestedDisposition, callDispositions, useApiCallDispositions]);
  const suggestionAlreadyApplied =
    suggestedDispositionCode !== undefined && suggestedDispositionCode === currentDispositionValue;
  const handleAcceptSuggestion = useCallback(() => {
    if (suggestedDispositionCode !== undefined) {
      handleCallDispositionSelect(suggestedDispositionCode);
    }
  }, [suggestedDispositionCode, handleCallDispositionSelect]);
  const acceptDisabled =
    !editingCallSections.output ||
    isSaving ||
    suggestionAlreadyApplied ||
    suggestedDispositionCode === undefined;
  const acceptDisabledTitle = !editingCallSections.output
    ? 'Click Edit to modify disposition'
    : suggestionAlreadyApplied
      ? 'Already set to suggestion'
      : suggestedDispositionCode === undefined
        ? 'Suggestion does not match an available option'
        : undefined;

  // Three-tab segmented control rendered next to the "Output" section title:
  // All / Missed / Required. Drives `filteredOutputGroups` above. Hidden when
  // the active source has no required fields — engine output for human calls,
  // call.disposition for AI calls — so unsupported workflows fall through to
  // today's full-form behavior.
  const headerOutputFilterTabs = useMemo<React.ReactNode>(() => {
    const required = activeRequiredFields.length;
    if (required === 0) return null;
    const missing = activeMissingFields.length;
    const pillBase =
      'inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold rounded-md ml-1.5';
    const missingPillColor =
      missing === 0 ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700';
    const requiredPillColor = 'bg-muted text-txt-secondary';
    return (
      <Tabs value={outputFilter} onValueChange={value => setOutputFilter(value as OutputFilter)}>
        <TabsList className="h-7">
          <TabsTrigger
            value={OutputFilter.All}
            className="text-xs h-6 px-2 text-muted-foreground data-[state=active]:text-foreground"
          >
            All
          </TabsTrigger>
          <TabsTrigger
            value={OutputFilter.Missed}
            className="text-xs h-6 px-2 text-muted-foreground data-[state=active]:text-foreground"
            aria-label={`Missed (${missing})`}
          >
            Missed
            <span className={`${pillBase} ${missingPillColor}`}>{missing}</span>
          </TabsTrigger>
          <TabsTrigger
            value={OutputFilter.Required}
            className="text-xs h-6 px-2 text-muted-foreground data-[state=active]:text-foreground"
            aria-label={`Required (${required})`}
          >
            Required
            <span className={`${pillBase} ${requiredPillColor}`}>{required}</span>
          </TabsTrigger>
        </TabsList>
      </Tabs>
    );
  }, [activeRequiredFields, activeMissingFields, outputFilter]);

  // Handle action selection - show confirmation dialog
  const handleActionSelect = (
    action: string,
    _caseIdParam?: string,
    actionDataParams?: ActionDataParams,
  ) => {
    if (!caseId) return;

    const statusUpdatePlan: StatusUpdatePlan = {
      action,
      caseIds: [caseId],
      count: 1,
      actionDataParams,
    };

    setConfirmationDialog({
      isOpen: true,
      statusUpdatePlans: [statusUpdatePlan],
    });
  };

  // Whether "Mark as Terminated" should appear in the More-options menu. Mirrors
  // exactly what StatusDropdown used to decide before the action was moved here:
  // the case must be in an actionable state AND the workflow's status config must
  // list mark_as_terminated for the current state (plus the canPerformAction
  // guard). Same handleActionSelect → StatusConfirmationDialog → executeAction
  // flow runs on click, so the API call and note/disposition gating are identical.
  const canTerminateFromMenu = useMemo(() => {
    if (!caseDetails?.state || !canWorkCaseHumanAudit) return false;
    const isActionableState =
      caseDetails.status_flags?.blocked_on_manual_task === true ||
      caseDetails.status_flags?.can_cancel_call === true ||
      caseDetails.state === CaseState.AI_AUDIT;
    if (!isActionableState) return false;
    const available = getAvailableActionsForCaseState(selectedWorkflow, caseDetails.state);
    return (
      available.some(a => a.value === 'mark_as_terminated') &&
      canPerformAction('mark_as_terminated', caseDetails.state, caseDetails)
    );
  }, [caseDetails, selectedWorkflow, canWorkCaseHumanAudit]);

  // Handle confirmation dialog response
  const handleConfirmAction = async (
    noteContent?: string,
    scheduledAt?: string,
    dispositionCode?: number,
  ) => {
    if (!caseId || !caseDetails || confirmationDialog.statusUpdatePlans.length === 0) {
      return;
    }

    const plan = confirmationDialog.statusUpdatePlans[0];
    const action = plan.action;

    if (actionRequiresCallDetails(action) && !callDetails) {
      return;
    }

    // Also write the case's latest human call's own disposition when the
    // agent picked one while finishing a human call (BI-1848). Scoped to the
    // human_call_* actions that end the step (incl. the BV-2514/BV-2657 move): the four
    // pre-existing close-case actions (mark_as_completed/mark_as_terminated/
    // complete/terminate) fire from human_audit, where there's no relevant call,
    // and must not gain this write.
    //
    // Awaited, and aborting the action on failure: the server's completion
    // guard reads this very disposition, so letting the ExecuteAction race the
    // PATCH would get a legitimate confirm rejected.
    if (
      (HUMAN_CALL_COMPLETION_ACTIONS.includes(action) ||
        action === 'human_call_mark_as_terminated') &&
      typeof dispositionCode === 'number'
    ) {
      try {
        await writeLatestHumanCallDisposition(dispositionCode);
      } catch {
        toast.error("Failed to update the call's disposition — the case was not routed.");
        return;
      }
    }

    // Merge note content and scheduled_at into action data params.
    const actionDataParams = {
      ...plan.actionDataParams,
      actionNote: noteContent,
      scheduled_at: scheduledAt,
      // Refill's Cancel Scheduled Call carries the operator's text through as the
      // cancellation reason too, not just as a note — the same thing the
      // cancel_case branch below does. Without it the reason reaching the workflow
      // is always the FSM's own default. Scoped to this action rather than applied
      // to every cancel, so no existing action's payload changes shape.
      ...(action === 'refill_cancel_scheduled_call' ? { cancelReason: noteContent } : {}),
    };

    // Handle cancel case action specifically
    if (action === 'cancel_case') {
      handleCancelCaseAction(
        caseId,
        executeAction.mutate,
        {
          onCloseDialog: () => setConfirmationDialog({ isOpen: false, statusUpdatePlans: [] }),
          onCloseModal: () => {
            // Navigate back to cases list
            const casesPath = buildRoutePath(
              '/cases',
              removeOrgPrefix(orgId || ''),
              removeProgPrefix(progId || ''),
            );
            navigate(casesPath);
          },
        },
        // Carry the text as the workflow cancel reason too; actionNote (set above)
        // already creates the Notes-section state_changed note.
        { ...actionDataParams, cancelReason: noteContent },
      );
      return;
    }

    // Handle cancel call action specifically
    if (action === 'cancel_call') {
      handleCancelCallAction(
        caseId,
        executeAction.mutate,
        {
          onCloseDialog: () => setConfirmationDialog({ isOpen: false, statusUpdatePlans: [] }),
        },
        actionDataParams,
      );
      return;
    }

    // Execute the action with workflow type
    const runAction = () =>
      executeCaseAction(
        action,
        caseId,
        selectedWorkflow?.workflow_type,
        executeAction.mutate,
        {
          // Staged escalations are persisted by now, so drop them. Only on SUCCESS —
          // a failed action must keep them for the retry.
          onSuccess: () => clearStagedEscalations(caseId),
          onCloseDialog: () => setConfirmationDialog({ isOpen: false, statusUpdatePlans: [] }),
          onCloseModal: () => {
            // Navigate back to cases list
            const casesPath = buildRoutePath(
              '/cases',
              removeOrgPrefix(orgId || ''),
              removeProgPrefix(progId || ''),
            );
            navigate(casesPath);
          },
        },
        actionDataParams,
        { caseState: caseDetails?.state },
      );

    // Escalations saved in the Human Audit composer are written HERE rather than at Save
    // time, via UpdateAudit on the in-progress human-audit entity. Writing them earlier
    // made that entity the newest audit carrying handoff actions, and the handoff list —
    // which reads the newest such audit — flipped from the AI auditor's items to the
    // freshly-escalated ones. Silent on success (the auditor was told when they saved);
    // an error surfaces and ABORTS the routing so the escalations are never lost
    // silently. The write must land BEFORE the action, which completes the audit.
    const stagedRows = (stagedEscalations[caseId] ?? []).filter(row => row.reason.trim() !== '');
    if (actionNameRoutesToHumanCall(action) && stagedRows.length > 0 && inProgressHumanAudit?.id) {
      updateAudit.mutate(
        {
          audit_id: inProgressHumanAudit.id,
          // UpdateAudit REPLACES the whole list, so merge onto what is already there.
          handoff_actions: {
            values: [
              ...(inProgressHumanAudit.handoff_actions ?? []),
              ...stagedRows.map(row => ({ field: row.field, note: row.reason.trim() })),
            ],
          },
        },
        {
          onSuccess: () => runAction(),
          onError: () => toast.error('Could not save the escalations — the case was not routed.'),
        },
      );
      return;
    }

    runAction();
  };

  const handleCancelAction = () => {
    setConfirmationDialog({ isOpen: false, statusUpdatePlans: [] });
  };

  // Handle assignee confirmation dialog
  const handleConfirmAssigneeChange = (noteContent?: string) => {
    const { assigneeUpdatePlan } = assigneeConfirmationDialog;

    if (!assigneeUpdatePlan || !caseDetails) {
      return;
    }

    setAssigneeConfirmationDialog({
      isOpen: false,
      assigneeUpdatePlan: null,
    });

    updateAssignment.mutate({
      caseId: caseDetails.id!,
      assigned: assigneeUpdatePlan.assigneeUserId,
      noteContent,
      teamId: assigneeUpdatePlan.teamId,
    });
  };

  const handleCancelAssigneeChange = () => {
    setAssigneeConfirmationDialog({
      isOpen: false,
      assigneeUpdatePlan: null,
    });
  };

  // Get campaign context from navigation state (when coming from campaign details)
  const fromCampaign = location.state?.fromCampaign;

  // Build breadcrumb items based on navigation context
  const breadcrumbItems = fromCampaign
    ? [
        <Breadcrumb.Item
          key="campaigns"
          to={buildRoutePath(
            '/campaigns',
            removeOrgPrefix(orgId || ''),
            removeProgPrefix(progId || ''),
          )}
        >
          Campaigns
        </Breadcrumb.Item>,
        <Breadcrumb.Item
          key="campaign-id"
          to={buildRoutePath(
            `/campaigns/${fromCampaign}`,
            removeOrgPrefix(orgId || ''),
            removeProgPrefix(progId || ''),
          )}
        >
          {fromCampaign}
        </Breadcrumb.Item>,
        <Breadcrumb.Item key="cases">Cases</Breadcrumb.Item>,
        <Breadcrumb.Item key="case-id">{formatCaseId(caseId || '')}</Breadcrumb.Item>,
      ]
    : [
        <Breadcrumb.Item
          key="cases"
          to={buildRoutePath(
            '/cases',
            removeOrgPrefix(orgId || ''),
            removeProgPrefix(progId || ''),
          )}
        >
          Cases
        </Breadcrumb.Item>,
        <Breadcrumb.Item key="case-id">{formatCaseId(caseId || '')}</Breadcrumb.Item>,
      ];

  // Render breadcrumb
  const breadcrumb = (
    <BreadcrumbPortal>
      <Breadcrumb>{breadcrumbItems}</Breadcrumb>
    </BreadcrumbPortal>
  );

  // Render header actions
  const headerActions = (
    <HeaderActionsPortal>
      {caseDetails && (
        <>
          {/* Download Case Button */}
          <DownloadDropdown
            onDownload={async (
              format: 'csv' | 'xlsx',
              callsOption?: CallsExportOption,
              includeEligibilityChecks?: boolean,
            ) => {
              // Always the reporting export, matching the cases-list download:
              // case metadata + input AND output columns (plus section
              // summaries). The campaign file format is input-only, so routing
              // the plain export through it dropped every output column from a
              // single-case download.
              if (selectedProgram?.id) {
                await downloadCaseRecords(
                  selectedProgram.id,
                  {
                    case_record_ids: [caseId!],
                  },
                  format,
                  includeEligibilityChecks || false,
                  `case_${formatCaseId(caseId!)}`,
                  callsOption || 'none',
                  selectedWorkflow?.workflow_type,
                );
              }
            }}
            onlyShowIcon={true}
            workflowType={selectedWorkflow?.workflow_type}
            showSOBOption={selectedProgram?.id ? isProgramSOBEnabled(selectedProgram.id) : false}
            onDownloadSOB={async () => {
              if (orgId && selectedProgram?.id) {
                await downloadIndividualSOB(caseId!, removeOrgPrefix(orgId), selectedProgram.id);
              }
            }}
            isDownloadingSOB={isDownloadingSOB}
            sobLabel="Download SOB"
          />

          {/* Download Document Button - only show when state is review_needed and document is available */}
          {caseDetails?.state === 'review_needed' && hasDocumentAsset && documentAssetId && (
            <Button
              onClick={() => {
                downloadDoc({
                  assetId: documentAssetId,
                  caseRecordId: caseId,
                  filename: `document_${documentAssetId}${getAssetFileExtension(documentAsset?.file_path, documentAsset?.content_type)}`,
                });
              }}
              title="Download document"
              variant="outline"
              size="sm"
            >
              <FileText className="w-4 h-4 mr-1" />
              Download Document
            </Button>
          )}

          {/* Twilio Call Button - show when state is human_call and case is assigned */}
          {isHumanCallState(caseDetails?.state) &&
            (!!caseDetails?.assigned || !!caseDetails?.assigned_user_id) && (
              <TwilioCallButton
                phoneNumber={
                  (caseDetails?.input_data?.data?.primary_phone as string) ||
                  getPhoneFromUISchema(
                    inputGroups,
                    caseDetails?.input_data?.data || caseDetails?.input_data,
                  ) ||
                  ''
                }
                caseRecordId={caseDetails?.id || ''}
                workflowName={
                  (selectedWorkflow as any)?.workflow_name || (selectedWorkflow as any)?.name || ''
                }
                workflowType={selectedWorkflow?.workflow_type}
                state={caseDetails?.state || ''}
                assignedUser={caseDetails?.assigned || null}
                assignedUserId={caseDetails?.assigned_user_id || null}
                callIndex={getRandomPhoneIndex()}
                activeTab={activeTab}
                blockedReason={newCallBlockReason}
              />
            )}

          {/* Status Dropdown — "Mark as Terminated" is surfaced in the
              "More options" menu below instead of here. */}
          <StatusDropdown
            item={caseDetails}
            selectedWorkflow={selectedWorkflow}
            onActionSelect={handleActionSelect}
            excludeActions={DETAILS_PAGE_EXCLUDED_STATUS_ACTIONS}
          />

          {/* More Options Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" title="More options">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {/* Add Note option */}
              <DropdownMenuItem
                onClick={() => setIsAddNoteDialogOpen(true)}
                className="cursor-pointer"
                disabled={!canAddNoteToCase(caseDetails?.state)}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Note
              </DropdownMenuItem>

              {/* View History option */}
              {!historyDisabled && (
                <DropdownMenuItem
                  onClick={() => setIsHistorySheetOpen(true)}
                  className="cursor-pointer"
                >
                  <History className="w-4 h-4 mr-1" />
                  View History
                </DropdownMenuItem>
              )}

              {/* View Timeline option. The timeline column lives in Summary only, so this
                  goes there before expanding it — otherwise the item silently does nothing
                  on every other tab. */}
              <DropdownMenuItem
                onClick={() => {
                  setActiveTab(CaseDetailsTabs.SUMMARY);
                  setIsTimelineCollapsed(false);
                }}
                className="cursor-pointer"
              >
                <Activity className="w-4 h-4 mr-1" />
                View Timeline
              </DropdownMenuItem>

              {/* Copy Link option */}
              <DropdownMenuItem
                onClick={() => {
                  const caseUrl = buildCaseUrl(caseDetails);
                  copyToClipboard(caseUrl, 'Link copied');
                }}
                className="cursor-pointer"
              >
                <Share2 className="w-4 h-4 mr-1" />
                Copy Link
              </DropdownMenuItem>

              {/* Clone Case - only for orgs with feature_flag/clone_case_<org_id> = true */}
              <CloneCaseMenuItem
                caseId={caseDetails.id!}
                organizationId={caseDetails.organization_id}
                iconClassName="w-4 h-4 mr-1"
              />

              {/* Mark as Terminated option — moved here from the status
                  dropdown. Reuses handleActionSelect so the confirmation +
                  disposition-required flow (PBM/UnifiedMedBV) is preserved. */}
              {canTerminateFromMenu && (
                <DropdownMenuItem
                  onClick={() => handleActionSelect('mark_as_terminated', caseDetails.id!)}
                  className="cursor-pointer"
                  disabled={executeAction.isPending}
                >
                  <Ban className="w-4 h-4 mr-1" />
                  {ACTION_LABELS.mark_as_terminated}
                </DropdownMenuItem>
              )}

              {/* Cancel Case option. Closing a Program-Admin-only human audit (BV-2657)
                  is reserved for those roles, like Mark as Terminated above. */}
              {canWorkCaseHumanAudit && canPerformAction('cancel_case', caseDetails?.state) && (
                <DropdownMenuItem
                  onClick={() => {
                    const statusUpdatePlan: StatusUpdatePlan = {
                      action: 'cancel_case',
                      caseIds: [caseDetails.id!],
                      count: 1,
                    };
                    setConfirmationDialog({
                      isOpen: true,
                      statusUpdatePlans: [statusUpdatePlan],
                    });
                  }}
                  className="cursor-pointer"
                  disabled={executeAction.isPending}
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Cancel Case
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Add Note Dialog */}
          <AddNoteDialog
            isOpen={isAddNoteDialogOpen}
            onClose={() => setIsAddNoteDialogOpen(false)}
            caseRecordId={caseDetails?.id || ''}
          />

          {/* Case History Sheet */}
          <CaseHistorySheet
            open={isHistorySheetOpen}
            onOpenChange={setIsHistorySheetOpen}
            caseRecordId={caseDetails?.id || ''}
            workflowId={selectedWorkflow?.id}
            schemaVersion={caseSchemaVersion(caseDetails)}
          />
        </>
      )}
    </HeaderActionsPortal>
  );

  // Show loading state
  if (loading) {
    return (
      <CaseDetailsShell
        schemaVersion={caseSchemaVersion(caseDetails)}
        breadcrumb={breadcrumb}
        headerActions={headerActions}
      >
        <div className="flex flex-col justify-center items-center h-full">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-foreground"></div>
          <p className="mt-2 text-sm text-muted-foreground">Loading...</p>
        </div>
      </CaseDetailsShell>
    );
  }

  // Show error state only if we have no data at all (initial load failed).
  // If we have stale data from a previous successful fetch, keep showing it.
  if (error && !caseDetails) {
    return (
      <CaseDetailsShell
        schemaVersion={caseSchemaVersion(caseDetails)}
        breadcrumb={breadcrumb}
        headerActions={headerActions}
      >
        <div className="p-6 h-full">
          <ErrorContainer
            errorMessage={error?.userMessage}
            buttonText={errorButtonConfig?.text}
            buttonOnClick={errorButtonConfig?.onClick}
          />
        </div>
      </CaseDetailsShell>
    );
  }

  // Main content
  // --- Review view panes -------------------------------------------------
  // Defined once because two layouts render them: the untouched three-column Review, and
  // the merged reference column (handoff | transcript | input) that makes room for the
  // Call Script when the latest human call is selected.
  const reviewTranscriptPane = (
    <div className="flex-1 min-h-0">
      <TranscriptErrorBoundary>
        <Transcript
          className="h-full"
          caseRecordId={caseId!}
          callId={selectedCallId!}
          callSid={callDetails?.sid}
          currentTime={audioCurrentTime}
          onSegmentClick={handleSegmentClick}
          callStatus={selectedCallStatus}
          assets={transcriptAssets}
          isLoadingAssets={transcriptAssetsLoading}
          enableSearch
          onTranscriptLoad={setTranscriptSegments}
          evidenceHighlightRange={evidenceHighlightRange}
          onEvidenceHighlightClear={() => setEvidenceHighlightRange(null)}
        />
      </TranscriptErrorBoundary>
    </div>
  );
  const reviewInputExpandAll = (
    <SimpleTooltip
      content={reviewInputExpanded.length === inputGroups.length ? 'Collapse All' : 'Expand All'}
      side="bottom"
    >
      <Button
        variant="outline"
        size="sm"
        onClick={handleReviewInputToggleAll}
        className="h-5 w-5 p-0"
      >
        <ChevronsDownUp className="w-3 h-3" />
      </Button>
    </SimpleTooltip>
  );
  const reviewInputPane = (
    <div className="flex-1 overflow-y-auto px-4 space-y-4">
      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white border rounded-lg p-3">
              <Skeleton className="h-5 w-32 mb-3" />
              <div className="space-y-3">
                {[...Array(4)].map((_, j) => (
                  <div key={j}>
                    <Skeleton className="h-3 w-24 mb-1" />
                    <Skeleton className="h-4 w-full" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : inputGroups.length > 0 ? (
        <FieldGroupAccordion
          groups={inputGroups}
          data={displayData.inputData}
          editableState={null}
          isEditable={false}
          section="input"
          workflowType={selectedWorkflow?.workflow_type}
          showCollapseAll={false}
          externalExpandedItems={reviewInputExpanded}
          onExpandedItemsChange={setReviewInputExpanded}
          defaultExpanded={true}
        />
      ) : (
        <FieldGroupAccordion
          groups={[
            {
              label: 'Input Data',
              fields: Object.keys(displayData.inputData || {}).map(key => ({
                id: key,
                label: formatFieldName(key),
                type: 'text' as const,
              })),
            },
          ]}
          data={displayData.inputData}
          editableState={null}
          isEditable={false}
          section="input"
          workflowType={selectedWorkflow?.workflow_type}
          showCollapseAll={false}
          externalExpandedItems={reviewInputExpanded}
          onExpandedItemsChange={setReviewInputExpanded}
          defaultExpanded={true}
          emptyStateMessage={{
            title: 'No Request Data Available',
            subtitle: 'Request data will appear here',
          }}
        />
      )}
    </div>
  );
  // The Call Script layout's Output header keeps only Edit / Save and the save error: the
  // title is dropped and the sidebar's collapse control sits on the field board's "All
  // fields" row instead. With neither to show, the row is left out rather than rendered
  // empty.
  const showReviewOutputHeader =
    !showReviewCallScript || canEditOutput || (editingCallSections.output && !!outputSaveError);
  const reviewOutputPane = (
    <>
      {showReviewOutputHeader && (
        <EditableSectionHeader
          title={showReviewCallScript ? '' : 'Output'}
          // Restored in BV-2120. These were dropped when the Review pane was reworked, so
          // the Edit/Save buttons rendered with no test id at all — the e2e suite could not
          // enter edit mode, and the disposition select below is disabled outside it
          // (`!editingCallSections.output`), which stalled every dial that needed to
          // dispose a previous call. Distinct from Summary (`edit-output-button`) and Human
          // Audit (`ha-edit-output-button`) because all three can be on screen at once.
          editTestId="review-edit-output-button"
          saveTestId="review-save-output-button"
          canEdit={canEditOutput}
          isEditing={editingCallSections.output}
          onEditStart={() => handleCallEditStart('output')}
          onSave={handleOutputSaveClick}
          onCancel={() => {
            setOutputFieldErrors({});
            handleCallEditCancel('output');
          }}
          isSaving={isSaving}
          errorMessage={outputSaveError}
          onErrorView={revealFirstOutputError}
          // Only in the classic layout. There the pane renders FieldGroupAccordion, whose
          // open sections this drives. In the Call Script layout the pane is the field
          // board, which owns its own collapse-all — leaving the header control there gave
          // a button that toggled nothing (BV-1575).
          showExpandButton={!showReviewCallScript}
          isExpanded={reviewOutputExpanded.length === outputGroups.length}
          onToggleExpand={handleReviewOutputToggleAll}
          // The field board below owns an identical All / Missed / Required filter, so the
          // header's would be a second control for the same thing in one pane (BV-1575).
          headerBadge={showReviewCallScript ? undefined : headerOutputFilterTabs}
        />
      )}
      {/* Loading indicator while the post_call_review workflow
                          extracts call data and merges it into the case record.
                          Both AI and human calls go through the same flow now —
                          there's no manual accept step. */}
      {callDetails?.status_flags?.call_data_extract_in_progress && (
        <div className="mx-4 mb-2 flex items-center gap-2 rounded-md border bg-muted px-3 py-2">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Reviewing call...</span>
        </div>
      )}
      {/* Frozen header strip — combined disposition selector +
                          assist (suggestion + ask-next). Stays put while the
                          field accordion below scrolls. */}
      {isHumanCallSelected &&
        (useApiCallDispositions || (editingCallSections.output && dispositionHelper.supported)) && (
          <div className="px-4 pb-2 pt-1.5 border-b bg-background">
            <div className="bg-card border rounded-lg px-2.5 py-2 space-y-1.5">
              {/* The disposition lives here, not in the dialpad —
                                  it is part of writing up the call, beside the
                                  fields it is derived from. Always visible;
                                  edit mode decides whether it is live. */}
              {useApiCallDispositions && (
                <div>
                  <div className="flex items-center justify-between gap-2 mb-0.5 min-w-0">
                    <label className="text-xs text-muted-foreground shrink-0">Disposition</label>
                    {editingCallSections.output && dispositionHelper.supported && (
                      <DispositionSuggestion
                        result={dispositionHelper}
                        showCaption={false}
                        onAcceptSuggestion={handleAcceptSuggestion}
                        acceptDisabled={acceptDisabled}
                        acceptDisabledTitle={acceptDisabledTitle}
                        className="justify-end"
                      />
                    )}
                  </div>
                  <SearchableSelect
                    value={currentDispositionValue}
                    onValueChange={value => handleCallDispositionSelect(value as number | '')}
                    options={callDispositionOptions}
                    placeholder={
                      isLoadingCallDispositions
                        ? 'Loading...'
                        : isSaving
                          ? 'Updating...'
                          : 'Select disposition...'
                    }
                    disabled={isLoadingCallDispositions || isSaving || !editingCallSections.output}
                    searchPlaceholder="Search dispositions..."
                    emptyText="No dispositions found"
                    className="h-8 text-sm"
                  />
                </div>
              )}
              <div>
                <label className="text-xs text-muted-foreground mb-0.5 block">Notes</label>
                <Textarea
                  value={draftDisposition?.notes ?? callDetails?.disposition?.notes ?? ''}
                  onChange={e =>
                    handleDispositionChange({
                      ...(draftDisposition ?? {}),
                      notes: e.target.value,
                    })
                  }
                  placeholder="Add any additional notes about the call..."
                  disabled={isSaving || !editingCallSections.output}
                  rows={1}
                  className="min-h-9 text-sm"
                />
              </div>
              {editingCallSections.output && dispositionHelper.supported && (
                <DispositionHelperPanel
                  result={dispositionHelper}
                  fieldLabels={outputFieldLabels}
                  fieldOrder={outputFieldOrder}
                  onJumpToField={handleJumpToField}
                  onAcceptSuggestion={useApiCallDispositions ? handleAcceptSuggestion : undefined}
                  acceptDisabled={acceptDisabled}
                  acceptDisabledTitle={acceptDisabledTitle}
                  // No Ask-next here: what to capture next is the Call Script's job, and the
                  // field board beside it already marks what is still missing. Defaults to
                  // true, so it has to be turned off explicitly.
                  showAskNext={false}
                  showSuggestion={!useApiCallDispositions}
                />
              )}
            </div>
          </div>
        )}
      {/* Who scrolls, and therefore what stays on screen, differs by layout (BV-2129).
          Classic: the accordion has no scroller of its own, so this wrapper is it.
          Call Script: the field board scrolls its OWN list beneath a frozen header, so
          this wrapper must not scroll — a block container with overflow-auto made the
          board's `flex-1 min-h-0` inert (they only mean something to a flex CHILD), so
          the board grew to content height and this element scrolled the lot, carrying
          the "All fields" heading, the filter and the search bar off screen. As a
          bounded flex column it hands its height down instead, and the board's own list
          becomes the scroller. */}
      <div
        ref={outputFieldsRef}
        className={cn(
          'flex-1',
          // No inset around the field board: it pads its own header and rows, so a second
          // px-4 / pt-3 here only narrowed the sidebar's working width (BV-2742).
          showReviewCallScript
            ? cn('flex min-h-0 flex-col overflow-hidden', loading && 'px-4 pt-3')
            : 'overflow-auto space-y-4 px-4 pt-3',
        )}
      >
        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white border rounded-lg p-3">
                <Skeleton className="h-5 w-32 mb-3" />
                <div className="space-y-3">
                  {[...Array(4)].map((_, j) => (
                    <div key={j}>
                      <Skeleton className="h-3 w-24 mb-1" />
                      <Skeleton className="h-4 w-full" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : showReviewCallScript ? (
          /* Call Script layout only (BV-1575): the pane's field list is the "All fields"
             board — every output leaf with its status dot, click-to-heard and inline edit,
             plus its own All / Missed / Required filter — instead of the read-oriented
             accordion every other call shows. Disposition and Notes are untouched above
             this in the frozen header strip. The board shares the script column's working
             set, so a value captured in either shows in both. */
          <ReviewOutputFieldBoard
            ref={reviewOutputBoardRef}
            caseId={caseId}
            // Save-time errors, the same map the header banner and its View action use —
            // so the field View jumps to also shows its own message (BV-2129). The
            // accordion branch below has always had this via `fieldError`; the board
            // replaced it in this layout and lost it.
            fieldErrors={outputFieldErrors}
            // Mirrors the workspace's live errors back up so the header banner can see
            // them — this layout's draft is the workspace's, which this component cannot
            // read from above the provider (LA-7358). A stable useState setter, as the
            // prop's effect dependency requires.
            onValidationErrorsChange={setScriptOutputErrors}
            editable={humanCallEditable}
            callActive={humanCallActive}
            // Leading, not trailing: the collapse control sits on the edge facing the
            // script it folds away from, mirroring the reference sidebar's on the opposite
            // side. Only in this layout is the Output pane a sidebar.
            leadingActions={
              <SidebarCollapseButton label="Output" onCollapse={() => setIsOutputCollapsed(true)} />
            }
          />
        ) : filteredOutputGroups.length > 0 ? (
          <FieldGroupAccordion
            groups={filteredOutputGroups}
            data={editingCallSections.output ? editedCallData.output : outputData}
            extractedData={callDetails?.extracted_data?.output?.data}
            extractedMetadata={reviewExtractedMetadata}
            outputMetadata={outputMetadataWithHeard}
            isHumanAudit={caseDetails?.state === CaseState.HUMAN_AUDIT}
            editableState={null}
            isEditable={editingCallSections.output}
            section="output"
            workflowType={selectedWorkflow?.workflow_type}
            getFieldSuggestionProps={
              editingCallSections.output && callDetails?.type === CallType.CALL_TYPE_HUMAN
                ? humanCallSuggestionProps
                : undefined
            }
            fieldError={outputFieldErrors}
            onDataChange={handleOutputDataChange}
            onFieldBlur={editingCallSections.output ? handleOutputCommit : undefined}
            onNavigateToCall={handleNavigateToCall}
            onNavigateToAudit={handleNavigateToAudit}
            showCollapseAll={false}
            externalExpandedItems={reviewOutputExpanded}
            onExpandedItemsChange={setReviewOutputExpanded}
            defaultExpanded={true}
            emptyStateMessage={{
              title: 'No Response Data Available',
              subtitle: 'Response data will appear here once processed',
            }}
            dispositionFields={outputDispositionFields}
            currentCallId={callDetails?.id}
          />
        ) : (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="text-muted-foreground mb-4">
              <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <p className="text-muted-foreground text-sm font-medium mb-1">
              {outputFilter === OutputFilter.Missed
                ? 'All required fields are filled'
                : outputFilter === OutputFilter.Required
                  ? 'No required fields for this scenario'
                  : 'No Response Data Available'}
            </p>
            <p className="text-muted-foreground text-xs">
              {outputFilter === OutputFilter.Missed || outputFilter === OutputFilter.Required
                ? 'Switch to All to see the full form'
                : 'Response data will appear here once processed'}
            </p>
          </div>
        )}
      </div>
    </>
  );

  return (
    <CaseDetailsShell
      schemaVersion={caseSchemaVersion(caseDetails)}
      breadcrumb={breadcrumb}
      headerActions={headerActions}
    >
      <div className="flex flex-col h-full">
        {/* Sub-header with tabs and metadata */}
        <div className="border-b bg-primary-foreground px-4 py-2">
          <div className="flex justify-between items-center">
            <div className="flex">
              {!caseLoading && (
                <Tabs
                  value={activeTab}
                  onValueChange={value => {
                    // Pause audio when switching away from Review (preserve playback position)
                    if (value === CaseDetailsTabs.SUMMARY && activeTab === CaseDetailsTabs.REVIEW) {
                      audioPlayerRef.current?.stop();
                    }
                    setActiveTab(value as CaseDetailsTabType);
                  }}
                >
                  <TabsList className="h-8">
                    <TabsTrigger value={CaseDetailsTabs.SUMMARY} className="text-xs px-3 py-1">
                      Summary
                    </TabsTrigger>
                    <SimpleTooltip
                      content={
                        !hasCallsData &&
                        !hasEligibilityChecksData &&
                        !hasGenericTasksData &&
                        !hasTextsData &&
                        !hasEmailsData &&
                        !hasAuditData &&
                        activeTab !== CaseDetailsTabs.REVIEW
                          ? 'No calls to display'
                          : ''
                      }
                      side="bottom"
                    >
                      <TabsTrigger
                        value={CaseDetailsTabs.REVIEW}
                        className="text-xs px-3 py-1"
                        disabled={
                          !hasCallsData &&
                          !hasEligibilityChecksData &&
                          !hasGenericTasksData &&
                          !hasTextsData &&
                          !hasEmailsData &&
                          !hasAuditData &&
                          activeTab !== CaseDetailsTabs.REVIEW
                        }
                      >
                        Review
                      </TabsTrigger>
                    </SimpleTooltip>
                  </TabsList>
                </Tabs>
              )}
            </div>
            <div className="justify-end flex items-center">
              {/* Created at */}
              {caseDetails?.created_at && (
                <>
                  <div className="inline-flex items-center gap-1 shrink-0">
                    <span className="text-sm text-muted-foreground">Created:</span>
                    <FormattedDateTime value={caseDetails.created_at} className="text-sm" />
                  </div>
                </>
              )}

              {/* Completed at — only for completed (workflow_completed_at) or
                  terminated (workflow_cancelled_at) cases; hidden while in-flight. */}
              {(caseDetails?.workflow_completed_at || caseDetails?.workflow_cancelled_at) && (
                <>
                  <Separator orientation="vertical" className="h-4 mx-2" />
                  <div className="inline-flex items-center gap-1 shrink-0">
                    <span className="text-sm text-muted-foreground">Completed:</span>
                    <FormattedDateTime
                      value={caseDetails.workflow_completed_at || caseDetails.workflow_cancelled_at}
                      className="text-sm"
                    />
                  </div>
                </>
              )}

              {/* Dynamic Fields - First 2 selected input fields */}
              {firstTwoSelectedFields.map((field, index) => (
                <React.Fragment key={field.label}>
                  {index > 0 && <Separator orientation="vertical" className="h-4 mx-2" />}
                  <div className="inline-flex items-center gap-1 shrink-0">
                    <span className="text-sm text-muted-foreground">{field.label}</span>
                    <span className="text-sm">{field.value}</span>
                  </div>
                </React.Fragment>
              ))}

              {/* AI Call Scheduled Time */}
              {caseDetails?.scheduled_tasks?.['ai-call-task'] && (
                <>
                  <Separator orientation="vertical" className="h-4 mx-2" />
                  <div className="inline-flex items-center gap-1 shrink-0">
                    <span className="text-sm text-muted-foreground">AI Call Scheduled:</span>
                    <FormattedDateTime
                      value={caseDetails.scheduled_tasks['ai-call-task']}
                      className="text-sm"
                    />
                  </div>
                </>
              )}

              <Separator orientation="vertical" className="h-5 mx-3" />
              {/* SLA Timer */}
              <SLATimer
                workflowLastStartedAt={caseDetails?.workflow_last_started_at}
                workflowDurationSeconds={caseDetails?.workflow_duration_seconds}
                slaMinutes={selectedWorkflow?.sla?.minutes}
                isBreached={caseDetails?.status_flags?.sla_breach}
                completedAt={
                  caseDetails?.workflow_completed_at || caseDetails?.workflow_cancelled_at
                }
                workflowState={caseDetails?.workflow_state}
                workflowType={selectedWorkflow?.workflow_type}
                caseState={caseDetails?.state}
                slaRiskEscalated={caseDetails?.status_flags?.sla_risk_escalated}
                workflowVersion={selectedWorkflow?.version}
                variant="details"
              />
              {/* Assigned to dropdown */}
              {caseDetails && (
                <div className="flex items-center gap-2">
                  <Separator orientation="vertical" className="h-5 mx-3" />
                  <span className="text-sm text-muted-foreground whitespace-nowrap">Assignee</span>
                  <AssigneeSelect
                    item={caseDetails}
                    onAssigneeSelect={(userId, caseId, teamId) => {
                      const assigneeUpdatePlan: AssigneeUpdatePlan = {
                        assigneeUserId: userId,
                        caseIds: [caseId],
                        count: 1,
                        teamId,
                      };

                      setAssigneeConfirmationDialog({
                        isOpen: true,
                        assigneeUpdatePlan,
                      });
                    }}
                    triggerClassName="h-8 border-none bg-transparent outline-hidden focus:ring-0 focus-visible:ring-0 ring-0 text-sm px-1"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tabs Navigation - Fixed below sub-header */}
        {(() => {
          const hasCallTabs = shouldShowCallTabs && unifiedTabItems.length > 0;

          if (hasCallTabs) {
            return (
              <CallsTabs
                tabItems={unifiedTabItems}
                selectedTabId={selectedCallId}
                onTabSelect={newTabId => {
                  if (newTabId !== selectedCallId) {
                    audioPlayerRef.current?.stop();
                    setAudioCurrentTime(0);
                  }
                  setSelectedCallId(newTabId);
                  setManuallySelectedCallId(newTabId); // Track manual selection
                }}
                onCancelCall={callId => {
                  if (callId !== selectedCallId) {
                    setSelectedCallId(callId);
                  }
                  const statusUpdatePlan: StatusUpdatePlan = {
                    action: 'cancel_call',
                    caseIds: [caseId!],
                    count: 1,
                  };
                  setConfirmationDialog({
                    isOpen: true,
                    statusUpdatePlans: [statusUpdatePlan],
                  });
                }}
                onRetryTask={onRetryTask}
                onUpdateChartNotes={updateChartNotes}
                workflowType={selectedWorkflow?.workflow_type}
                isHumanAudit={isHumanAuditState(caseDetails?.state)}
              />
            );
          }

          return null;
        })()}

        {/* Main Content Area */}
        <div className="flex-1 overflow-hidden">
          {caseDetails && inputGroups.length > 0 && (
            <div className="flex flex-col h-full">
              {/* Conditional rendering based on view mode */}
              {activeTab === CaseDetailsTabs.SUMMARY ? (
                /* Summary View */
                <CaseSummaryView
                  caseId={caseId!}
                  caseDetails={caseDetails}
                  inputGroups={schemaInputGroups}
                  outputGroups={schemaOutputGroups}
                  canEditInput={getEditability.input}
                  canEditOutput={getEditability.output}
                  isHumanAudit={isHumanAuditState(caseDetails?.state)}
                  workflowType={selectedWorkflow?.workflow_type}
                  linkedCaseRecordIds={caseDetails?.linked_case_record_ids}
                  isNotesCollapsed={isNotesCollapsed}
                  onNotesCollapsedChange={setIsNotesCollapsed}
                  isTimelineCollapsed={isTimelineCollapsed}
                  onTimelineCollapsedChange={setIsTimelineCollapsed}
                  onNavigateToCall={handleNavigateToCall}
                  onNavigateToAudit={handleNavigateToAudit}
                />
              ) : activeTab === CaseDetailsTabs.REVIEW &&
                selectedTabItem?.type === 'human_audit' &&
                selectedAudit?.status === AuditStatus.AUDIT_STATUS_IN_PROGRESS ? (
                /* Human Audit — IN PROGRESS: live editing workspace (transcripts + editable output). */
                <HumanAuditTab
                  caseId={caseId!}
                  caseDetails={caseDetails}
                  calls={callsData?.calls ?? []}
                />
              ) : activeTab === CaseDetailsTabs.REVIEW &&
                (selectedTabItem?.type === 'ai_audit' ||
                  selectedTabItem?.type === 'human_audit') ? (
                /* AI Audit, or COMPLETED Human Audit — corrections diff view. */
                (() => {
                  const hasReasoning = !!selectedAudit?.decision_rationale;
                  const hasHandoff = auditorHandoffActions.length > 0;
                  const visibleAccordionItems = [
                    ...(hasHandoff ? ['handoff'] : []),
                    ...(hasReasoning ? ['reasoning'] : []),
                    ...auditorVisibleGroups.map((_, i) => i.toString()),
                  ];
                  const allAuditorGroupsExpanded =
                    visibleAccordionItems.length > 0 &&
                    visibleAccordionItems.every(idx => auditorGroupsExpanded.includes(idx));
                  const toggleAllAuditorGroups = () => {
                    setAuditorGroupsExpanded(allAuditorGroupsExpanded ? [] : visibleAccordionItems);
                  };

                  return (
                    <div className="flex flex-1 min-h-0">
                      {/* Left-side collapsible Input drawer */}
                      <div
                        className={`shrink-0 border-r flex transition-all duration-200 ${
                          isAiAuditInputCollapsed ? 'w-10' : 'w-[360px]'
                        }`}
                      >
                        {isAiAuditInputCollapsed ? (
                          <div className="flex flex-col items-center py-3 w-10 shrink-0">
                            <SimpleTooltip content="Expand Input" side="right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setIsAiAuditInputCollapsed(false)}
                                className="h-6 w-6 p-0"
                              >
                                <PanelLeftOpen className="w-4 h-4" />
                              </Button>
                            </SimpleTooltip>
                            <span className="text-xs text-muted-foreground mt-2 [writing-mode:vertical-lr]">
                              Input
                            </span>
                          </div>
                        ) : (
                          <div className="flex-1 min-w-0 flex flex-col">
                            <div className="px-4 py-2">
                              <div className="flex items-center justify-between">
                                <h3 className="text-muted-foreground font-medium text-sm">Input</h3>
                                <div className="flex items-center gap-1">
                                  <SimpleTooltip
                                    content={
                                      reviewInputExpanded.length === inputGroups.length
                                        ? 'Collapse All'
                                        : 'Expand All'
                                    }
                                    side="bottom"
                                  >
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={handleReviewInputToggleAll}
                                      className="h-5 w-5 p-0"
                                    >
                                      <ChevronsDownUp className="w-3 h-3" />
                                    </Button>
                                  </SimpleTooltip>
                                  <SimpleTooltip content="Collapse Input" side="bottom">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setIsAiAuditInputCollapsed(true)}
                                      className="h-6 w-6 p-0"
                                    >
                                      <PanelLeftClose className="w-4 h-4" />
                                    </Button>
                                  </SimpleTooltip>
                                </div>
                              </div>
                            </div>
                            <div className="flex-1 overflow-y-auto px-4 space-y-4">
                              {loading ? (
                                <div className="space-y-4">
                                  {[...Array(3)].map((_, i) => (
                                    <div key={i} className="bg-white border rounded-lg p-3">
                                      <Skeleton className="h-5 w-32 mb-3" />
                                      <div className="space-y-3">
                                        {[...Array(4)].map((_, j) => (
                                          <div key={j}>
                                            <Skeleton className="h-3 w-24 mb-1" />
                                            <Skeleton className="h-4 w-full" />
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : inputGroups.length > 0 ? (
                                <FieldGroupAccordion
                                  groups={inputGroups}
                                  data={displayData.inputData}
                                  editableState={null}
                                  isEditable={false}
                                  section="input"
                                  workflowType={selectedWorkflow?.workflow_type}
                                  showCollapseAll={false}
                                  externalExpandedItems={reviewInputExpanded}
                                  onExpandedItemsChange={setReviewInputExpanded}
                                  defaultExpanded={true}
                                />
                              ) : (
                                <FieldGroupAccordion
                                  groups={[
                                    {
                                      label: 'Input Data',
                                      fields: Object.keys(displayData.inputData || {}).map(key => ({
                                        id: key,
                                        label: formatFieldName(key),
                                        type: 'text' as const,
                                      })),
                                    },
                                  ]}
                                  data={displayData.inputData}
                                  editableState={null}
                                  isEditable={false}
                                  section="input"
                                  workflowType={selectedWorkflow?.workflow_type}
                                  showCollapseAll={false}
                                  externalExpandedItems={reviewInputExpanded}
                                  onExpandedItemsChange={setReviewInputExpanded}
                                  defaultExpanded={true}
                                  emptyStateMessage={{
                                    title: 'No Request Data Available',
                                    subtitle: 'Request data will appear here',
                                  }}
                                />
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Main content area — audit output. The Feedback view is
                          presented as a separate right-side drawer with its own
                          collapse toggle. */}
                      <div className="flex-1 min-w-0 flex flex-col">
                        <div className="px-4 py-2 flex items-center justify-between gap-2">
                          <h3 className="text-muted-foreground font-medium text-sm">Case Audit</h3>
                          {visibleAccordionItems.length > 0 && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-5 px-2 text-xs"
                              onClick={toggleAllAuditorGroups}
                              aria-label={allAuditorGroupsExpanded ? 'Collapse All' : 'Expand All'}
                              title={allAuditorGroupsExpanded ? 'Collapse All' : 'Expand All'}
                            >
                              <ChevronsDownUp className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                        <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-4">
                          <AuditorOutputFields
                            expanded={auditorGroupsExpanded}
                            onExpandedChange={setAuditorGroupsExpanded}
                            workflowType={selectedWorkflow?.workflow_type}
                            reasoning={
                              // Human audits have no AI reasoning — omit the section entirely
                              // (undefined → AuditorOutputFields renders no reasoning accordion).
                              selectedAudit?.type ===
                              AuditType.AUDIT_TYPE_HUMAN ? undefined : selectedAudit?.decision_rationale ? (
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                  {selectedAudit.decision_rationale}
                                </ReactMarkdown>
                              ) : (
                                <p className="text-muted-foreground italic">
                                  No reasoning provided.
                                </p>
                              )
                            }
                            recommendation={selectedAudit?.recommendation}
                            handoffActions={auditorHandoffActions}
                            groups={auditorVisibleGroups}
                            // Handoff labels resolve against the UNFILTERED schema —
                            // auditorVisibleGroups is narrowed by the changed-only filter.
                            schemaGroups={outputGroups}
                            fieldEnumMap={fieldEnumMap}
                            // AI-extraction context, so nested fields render the same
                            // chain of thought and AI-suggested value they already do on
                            // Summary and in the call-output panel (UAT-1571). Case-level
                            // on purpose: the audit tab is a case surface, and it is the
                            // Summary comparison the annotations must match. No
                            // `onAcceptExtracted` / `isHumanAudit` — an audit view shows
                            // suggestions, it never accepts them.
                            outputMetadata={outputDataMetadata}
                            extractedData={caseDetails?.extracted_data?.output?.data}
                            extractedMetadata={
                              caseDetails?.extracted_data?.output?.metadata as
                                | { fields?: Record<string, unknown> }
                                | undefined
                            }
                            onNavigateToCall={handleNavigateToCall}
                            onNavigateToAudit={handleNavigateToAudit}
                            getValue={auditFieldValue}
                            // Snapshot-bearing audits are the single source of their own
                            // before-values, down to each nested leaf — no correction's
                            // original_value is consulted alongside it.
                            snapshotBacked={!!selectedAudit?.output_snapshot}
                            getCorrection={fieldPath => correctionsByField[fieldPath]}
                            getLeafCorrections={fieldPath => leafCorrectionsByRoot[fieldPath]}
                            changedFieldCount={changedFieldCount}
                            onRevertField={getEditability.output ? revertAuditField : undefined}
                            isFieldReverted={isAuditFieldReverted}
                            showChangedOnly={showChangedOnly && visibleChangedFieldIds.size > 0}
                            onToggleChangedOnly={() => setShowChangedOnly(v => !v)}
                            onSubmitFeedback={(answer, note) => {
                              const auditId = selectedAudit?.id;
                              if (!auditId) {
                                toast.error('No audit row to submit feedback for');
                                return;
                              }
                              createAuditFeedback.mutate(
                                {
                                  audit_id: auditId,
                                  scope: 'case',
                                  verdict: answer === 'yes' ? 'AI_CORRECT' : 'HUMAN_CORRECT',
                                  feedback: note,
                                },
                                {
                                  onSuccess: () => toast.success('Feedback submitted'),
                                  onError: err =>
                                    toast.error(
                                      err instanceof Error
                                        ? err.message
                                        : 'Failed to submit feedback',
                                    ),
                                },
                              );
                            }}
                          />
                        </div>
                      </div>

                      {/* Right-side Feedback drawer — independently collapsible.
                          Lists feedback entries in timeline format. */}
                      <CollapsibleSidebar
                        label={`Feedback (${caseAudit.feedback?.length ?? 0})`}
                        isCollapsed={isAiAuditFeedbackCollapsed}
                        onCollapsedChange={setIsAiAuditFeedbackCollapsed}
                      >
                        <div className="flex-1 overflow-y-auto px-4 py-3 min-h-0">
                          <AuditFeedbackTable
                            feedback={caseAudit.feedback ?? []}
                            isLoading={caseAudit.isLoading}
                          />
                        </div>
                      </CollapsibleSidebar>
                    </div>
                  );
                })()
              ) : (
                /* Review View — three flexible columns beside the collapsible notes and
                   timeline. For the latest human call the first two merge into one tabbed
                   reference column so the Call Script can take a column of its own; every
                   other call keeps the original transcript / input / output split. */
                <>
                  {/* Main Content Area */}
                  <div className="flex flex-1 min-h-0">
                    {showReviewCallScript ? (
                      /* One provider around BOTH panes: the Call Script column and the
                         Output board share a single `useHumanCallWorkspace`, so a value
                         captured in either lands in the same working set instead of two
                         copies racing each other to persist (BV-1575). It renders a
                         Fragment, so the flex row is unchanged, and mounts only here —
                         the workspace still never runs for calls without a script. */
                      <HumanCallWorkspaceProvider
                        caseId={caseId}
                        caseDetails={caseDetails}
                        // The call the script reads and writes: the SELECTED one, so the
                        // post-hang-up write-up persists to the call it belongs to. Undefined
                        // on the pre-call preview, where the case's output is the baseline.
                        callDetails={callDetails ?? undefined}
                        editable={humanCallEditable}
                        callActive={humanCallActive}
                      >
                        <>
                          {/* Merged reference column — the auditor's handoff notes, the
                            transcript, and the case inputs share one column so the Call
                            Script gets a column of its own without squeezing the page
                            into five.

                            A left-hand CollapsibleSidebar, mirroring Output on the right:
                            fixed width rather than flex-1, so whenever either sidebar folds
                            away the freed space all goes to the script (the only column
                            that grows) instead of being split. The collapsed strip is
                            labelled with the pane that was showing, so it says what
                            expanding brings back. */}
                          <CollapsibleSidebar
                            side="left"
                            label={REVIEW_LEFT_TAB_LABEL[effectiveReviewLeftTab]}
                            isCollapsed={isReferenceCollapsed}
                            onCollapsedChange={setIsReferenceCollapsed}
                            expandedWidth="w-[440px]"
                            // The pane switcher below is this panel's header — it names the
                            // panes and carries the collapse control, so the sidebar's own
                            // chrome would only add an empty row.
                            headerless
                          >
                            <div className="flex shrink-0 items-center gap-1 border-b px-3 py-2">
                              {reviewLeftTabs.map(tab => (
                                <button
                                  key={tab}
                                  type="button"
                                  onClick={() => setReviewLeftTab(tab)}
                                  aria-pressed={effectiveReviewLeftTab === tab}
                                  className={cn(
                                    'rounded px-2 py-1 text-xs font-medium transition-colors',
                                    effectiveReviewLeftTab === tab
                                      ? 'bg-secondary text-foreground'
                                      : 'text-muted-foreground hover:text-foreground',
                                  )}
                                >
                                  {REVIEW_LEFT_TAB_LABEL[tab]}
                                </button>
                              ))}
                              {/* Pinned right: the Input pane's own expand/collapse-all (the
                                tab already names the pane) and the sidebar's collapse
                                control, which sits on the edge facing the script. */}
                              <span className="ml-auto flex items-center gap-1">
                                {effectiveReviewLeftTab === 'input' && reviewInputExpandAll}
                                <SidebarCollapseButton
                                  side="left"
                                  label={REVIEW_LEFT_TAB_LABEL[effectiveReviewLeftTab]}
                                  onCollapse={() => setIsReferenceCollapsed(true)}
                                />
                              </span>
                            </div>
                            {effectiveReviewLeftTab === 'handoff' ? (
                              <div className="flex-1 min-h-0 overflow-y-auto">
                                <HandoffNotesPanel
                                  handoffActions={callerHandoffActions}
                                  outputGroups={outputGroups}
                                />
                              </div>
                            ) : effectiveReviewLeftTab === 'transcript' ? (
                              reviewTranscriptPane
                            ) : (
                              reviewInputPane
                            )}
                          </CollapsibleSidebar>

                          {/* Call Script — the guided flow the caller works through: reading
                            from the case's output before a call, capturing into the live
                            call's while one is up, then read-only for the write-up. */}
                          <div className="flex-1 min-w-0 border-r flex flex-col">
                            <ReviewCallScriptColumn
                              caseId={caseId}
                              editable={humanCallEditable}
                              callActive={humanCallActive}
                              // Same tree the Output board reads: call-source stamped and
                              // heard-seek merged. Passed unconditionally, NOT gated on the
                              // phase: the tree gates itself. Before extraction the call has
                              // no metadata, so mergeHeardSeek yields heard entries only —
                              // which is exactly right, since a heard mark is call data and
                              // needs no extraction to be seekable. Evidence and
                              // chain-of-thought simply are not in the tree until extraction
                              // adds them. Phase-gating this held the ear symbol back until
                              // PCE finished, leaving the "on recording" chip with no way to
                              // jump to the recording (BV-1575).
                              outputMetaFields={
                                outputMetadataWithHeard?.fields as
                                  | Record<string, unknown>
                                  | undefined
                              }
                              // The call's EXTRACTED metadata — where the per-field evidence
                              // and reasoning actually live. `output_data.__metadata__` is
                              // effectively never written (zero calls carry it on dev/QA),
                              // so wiring only that left the script with nothing to show
                              // while the Output board, which passes both, had hundreds of
                              // evidence nodes to work from (BV-1575).
                              extractedMetaFields={
                                reviewExtractedMetadata?.fields as
                                  | Record<string, unknown>
                                  | undefined
                              }
                              // The extracted VALUES behind that metadata — what PCE read
                              // off the call. Drives the per-field "Accept" affordance.
                              extractedData={
                                callDetails?.extracted_data?.output?.data as
                                  | Record<string, unknown>
                                  | undefined
                              }
                              onNavigateToCall={handleNavigateToCall}
                              onNavigateToAudit={handleNavigateToAudit}
                            />
                          </div>
                        </>
                        {/* Output moves into a collapsible right sidebar so the script and its
                            reference material get the width. Only in this layout — every other
                            call keeps Output as a plain third column. */}
                        <CollapsibleSidebar
                          label="Output"
                          isCollapsed={isOutputCollapsed}
                          onCollapsedChange={setIsOutputCollapsed}
                          expandedWidth="w-[420px]"
                          // The pane's own "Output" header (edit/save, the All/Missed/Required
                          // pills) is this panel's header, and carries the collapse control —
                          // the sidebar's own chrome would only add an empty row above it.
                          headerless
                        >
                          {reviewOutputPane}
                        </CollapsibleSidebar>
                      </HumanCallWorkspaceProvider>
                    ) : (
                      <>
                        <>
                          {/* Column 1 - Transcript */}
                          <div className="flex-1 min-w-0 border-r flex flex-col">
                            <div className="px-4 py-2">
                              <h3 className="text-muted-foreground font-medium text-sm">
                                Transcript
                              </h3>
                            </div>
                            {reviewTranscriptPane}
                          </div>

                          {/* Column 2 - Input */}
                          <div className="flex-1 min-w-0 border-r flex flex-col">
                            <div className="px-4 py-2">
                              <div className="flex items-center justify-between">
                                <h3 className="text-muted-foreground font-medium text-sm">Input</h3>
                                {reviewInputExpandAll}
                              </div>
                            </div>
                            {reviewInputPane}
                          </div>
                        </>
                        {/* Column 3 - Output */}
                        <div className="flex-1 min-w-0 flex flex-col">{reviewOutputPane}</div>
                      </>
                    )}

                    {/* Notes and Timeline are deliberately NOT here: they belong to the
                        Summary view, which renders its own pair. Review is the working
                        surface (transcript / script / output) and the two extra sidebars
                        only competed with it for width. The collapse state still lives on
                        this page because Summary is driven by it. */}
                  </div>
                </>
              )}

              {/* Audio Player - kept mounted across tab switches to preserve playback position.
                  Visibility is controlled via CSS (hidden) rather than conditional rendering
                  to prevent the audio element from being destroyed and recreated. */}
              <div
                className={`border-t border${!hasAudioAsset || displayData.type !== 'call' || activeTab !== CaseDetailsTabs.REVIEW ? ' hidden' : ''}`}
              >
                <AudioErrorBoundary>
                  <AudioPlayer
                    ref={audioPlayerRef}
                    caseRecordId={caseId!}
                    caseCode={caseDetails?.code}
                    callId={selectedCallId!}
                    callDetails={callDetails}
                    onTimeUpdate={setAudioCurrentTime}
                    assets={audioAssets}
                  />
                </AudioErrorBoundary>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Dialog */}
      <StatusConfirmationDialog
        isOpen={confirmationDialog.isOpen}
        onClose={handleCancelAction}
        onConfirm={handleConfirmAction}
        callDispositionBlockReason={humanCallCompletionBlockReason}
        callMissingDisposition={latestHumanCallMissingDisposition}
        validateBeforeConfirm={validateOutputBeforeConfirm}
        statusUpdatePlans={confirmationDialog.statusUpdatePlans}
        workflow={selectedWorkflow}
        cases={caseDetails ? [caseDetails] : []}
        // The call-disposition PATCH is awaited BEFORE the action fires (see
        // handleConfirmAction), so it must count as loading too — otherwise
        // Confirm sits enabled for a whole round-trip after the first click.
        isLoading={executeAction.isPending || updateLatestHumanCallDisposition.isPending}
        isIndividual={true}
      />

      {/* Assignee Confirmation Dialog */}
      <AssigneeConfirmationDialog
        isOpen={assigneeConfirmationDialog.isOpen}
        onClose={handleCancelAssigneeChange}
        onConfirm={handleConfirmAssigneeChange}
        assigneeUpdatePlan={assigneeConfirmationDialog.assigneeUpdatePlan}
        cases={caseDetails ? [caseDetails] : []}
        isLoading={updateAssignment.isPending}
        isIndividual={true}
      />
    </CaseDetailsShell>
  );
};
